import {
    Activity as ActivityIcon,
    ArrowLeft,
    Building2,
    Bus,
    Calendar,
    Car,
    DollarSign,
    ExternalLink,
    FileText,
    GripVertical,
    Hotel,
    ImageIcon,
    IndianRupee,
    MapPin,
    Plane,
    Plus,
    Save,
    Train,
    Trash2,
    Upload,
    Users,
    X
} from "lucide-react";
import nunjucks from "nunjucks/browser/nunjucks.js";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { v4 as uuid } from "uuid";
import { useAuth } from "../contexts/AuthContext";
import pdfTemplate from "../templates/pdf_template.html";
import { api } from "../utils/api";
import { CatalogManagement } from "./CatalogManagement";
import { AirlineAutocomplete } from "./ui/AirlineAutocomplete";
import { AirportAutocomplete } from "./ui/AirportAutocomplete";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

// Polyfill String.prototype.format to mimic Python's format function (specifically "{:,}".format)
if (!String.prototype.format) {
    String.prototype.format = function (...args) {
        if (this.toString() === "{:,}") {
            if (args[0] === null || args[0] === undefined) return "";
            const val = Number(args[0]);
            if (isNaN(val)) return args[0];
            return val.toLocaleString("en-US");
        }
        return this.replace(/{(\d+)}/g, (match, number) => {
            return typeof args[number] !== "undefined" ? args[number] : match;
        });
    };
}

// Polyfill Array.prototype.append to mimic Python list's append method in Nunjucks templates
if (!Array.prototype.append) {
    Array.prototype.append = function (item) {
        this.push(item);
        return "";
    };
}

let nunjucksEnv;
const getTemplateEnv = () => {
    if (!nunjucksEnv) {
        nunjucksEnv = new nunjucks.Environment();
        nunjucksEnv.addFilter("int", (val) => parseInt(val, 10) || 0);
        nunjucksEnv.addFilter("length", (val) => (val ? val.length : 0));
        nunjucksEnv.addGlobal("range", (n) => {
            const limit = parseInt(n, 10);
            if (isNaN(limit) || limit <= 0) return [];
            const arr = [];
            for (let i = 0; i < limit; i++) {
                arr.push(i);
            }
            return arr;
        });
    }
    return nunjucksEnv;
};


const convertQuotationDataToINR = (data, rate) => {
    if (rate === 1) return data;

    // Create a deep copy
    const copy = JSON.parse(JSON.stringify(data));

    // Convert pricing costs
    if (copy.pricing) {
        const pricingKeys = [
            "airCost",
            "visaCost",
            "railwayCost",
            "packageCost",
            "taxes",
            "discount",
            "total",
            "perPerson",
            "depositDue"
        ];
        pricingKeys.forEach((key) => {
            if (copy.pricing[key] !== undefined && copy.pricing[key] !== null) {
                copy.pricing[key] = Math.ceil(Number(copy.pricing[key]) * rate);
            }
        });
    }

    // Convert flights costs
    if (Array.isArray(copy.flights)) {
        copy.flights.forEach((flight) => {
            if (flight.total_cost !== undefined) {
                flight.total_cost = Math.ceil(Number(flight.total_cost) * rate);
            }
            if (flight.cost_per_person !== undefined) {
                flight.cost_per_person = Math.ceil(Number(flight.cost_per_person) * rate);
            }
        });
    }

    // Convert visas costs
    if (Array.isArray(copy.visas)) {
        copy.visas.forEach((visa) => {
            if (visa.total_cost !== undefined) {
                visa.total_cost = Math.ceil(Number(visa.total_cost) * rate);
            }
            if (visa.cost_per_person !== undefined) {
                visa.cost_per_person = Math.ceil(Number(visa.cost_per_person) * rate);
            }
        });
    }

    // Convert transports costs
    if (Array.isArray(copy.transports_within_city)) {
        copy.transports_within_city.forEach((t) => {
            if (t.cost_per_vehicle !== undefined) {
                t.cost_per_vehicle = Math.ceil(Number(t.cost_per_vehicle) * rate);
            }
            if (t.total_cost !== undefined) {
                t.total_cost = Math.ceil(Number(t.total_cost) * rate);
            }
        });
    }

    // Convert MICE events costs
    if (Array.isArray(copy.mice_events)) {
        copy.mice_events.forEach((e) => {
            if (e.total_cost !== undefined) {
                e.total_cost = Math.ceil(Number(e.total_cost) * rate);
            }
            if (e.cost_per_person !== undefined) {
                e.cost_per_person = Math.ceil(Number(e.cost_per_person) * rate);
            }
            if (
                e.catering_details &&
                e.catering_details.cost_per_person !== undefined
            ) {
                e.catering_details.cost_per_person = Math.ceil(
                    Number(e.catering_details.cost_per_person) * rate
                );
            }
        });
    }

    // Convert standalone hotels costs
    if (Array.isArray(copy.standalone_hotels)) {
        copy.standalone_hotels.forEach((h) => {
            if (h.cost_per_room_per_night !== undefined) {
                h.cost_per_room_per_night = Math.ceil(
                    Number(h.cost_per_room_per_night) * rate
                );
            }
            if (h.total_cost !== undefined) {
                h.total_cost = Math.ceil(Number(h.total_cost) * rate);
            }
        });
    }

    // Convert sightseeing packages costs
    if (Array.isArray(copy.sightseeing_packages)) {
        copy.sightseeing_packages.forEach((s) => {
            if (s.total_cost !== undefined) {
                s.total_cost = Math.ceil(Number(s.total_cost) * rate);
            }
            if (s.cost_per_person !== undefined) {
                s.cost_per_person = Math.ceil(Number(s.cost_per_person) * rate);
            }
        });
    }

    // Convert trains costs
    if (Array.isArray(copy.trains)) {
        copy.trains.forEach((t) => {
            if (t.total_cost !== undefined) {
                t.total_cost = Math.ceil(Number(t.total_cost) * rate);
            }
            if (t.cost_per_person !== undefined) {
                t.cost_per_person = Math.ceil(Number(t.cost_per_person) * rate);
            }
        });
    }

    // Convert buses costs
    if (Array.isArray(copy.buses)) {
        copy.buses.forEach((b) => {
            if (b.total_cost !== undefined) {
                b.total_cost = Math.ceil(Number(b.total_cost) * rate);
            }
        });
    }

    // Convert days itinerary costs
    if (Array.isArray(copy.days)) {
        copy.days.forEach((d) => {
            if (d.cost_per_person !== undefined) {
                d.cost_per_person = Math.ceil(Number(d.cost_per_person) * rate);
            }
            if (d.total_cost !== undefined) {
                d.total_cost = Math.ceil(Number(d.total_cost) * rate);
            }
        });
    }

    return copy;
};


const COVER_IMAGE_OPTIONS = [
    "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1600&h=900&fit=crop",
    "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1600&h=900&fit=crop",
    "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1600&h=900&fit=crop",
    "https://images.unsplash.com/photo-1511578314322-379afb476865?w=1600&h=900&fit=crop",
    "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1600&h=900&fit=crop",
    "https://images.unsplash.com/photo-1596436889106-be35e843f974?w=1600&h=900&fit=crop",
    "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=1600&h=900&fit=crop",
    "https://images.unsplash.com/photo-1474487548417-781cb71495f3?w=1600&h=900&fit=crop"
];

const QuotationBuilder = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { request: stateRequest, quotation_id, status } = location.state || {};
    const [request, setRequest] = useState(stateRequest || null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [adminSettings, setAdminSettings] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [hotels, setHotels] = useState([]);
    const [activities, setActivities] = useState([]);
    const [activityFilter, setActivityFilter] = useState("All");
    const [miceEvents, setMiceEvents] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [showAddHotelModal, setShowAddHotelModal] = useState(false);
    const [showSightSeeingModal, setShowSightSeeingModal] = useState(false);
    const [showTransportModal, setShowTransportModal] = useState(false);
    const [showAddMiceEventModal, setShowAddMiceEventModal] = useState(false);
    const [showHotelModal, setShowHotelModal] = useState(false);
    const [showFlightModal, setShowFlightModal] = useState(false);
    const [flights, setFlights] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");

    const [showVisaModal, setShowVisaModal] = useState(false);
    const [visas, setVisas] = useState([]);

    const [showCreateCatalogModal, setShowCreateCatalogModal] = useState(false);
    const [createCatalogCategory, setCreateCatalogCategory] = useState("");

    const [detailedTerms, setDetailedTerms] = useState("");
    const [privacyPolicy, setPrivacyPolicy] = useState("");
    const [testimonials, setTestimonials] = useState([]);

    const [expiry_date, setExpiryDate] = useState("");
    const [orientation, setOrientation] = useState("portrait");
    const [showAdvanceWarning, setShowAdvanceWarning] = useState(false);
    const [pendingSaveParams, setPendingSaveParams] = useState(null);
    const [requestId, setRequestId] = useState(request?.id || null);
    const [costBreakup, setCostBreakup] = useState([]);
    const [showCurrencyPanel, setShowCurrencyPanel] = useState(false);
    const [currencyRates, setCurrencyRates] = useState([]);
    const [inputCurrency, setInputCurrency] = useState("INR");
    const [inputCurrencyRate, setInputCurrencyRate] = useState(1);
    const [isCustomCurrency, setIsCustomCurrency] = useState(false);

    // Custom S3 Cover Images list and uploading states
    const [coverImagesList, setCoverImagesList] = useState([]);
    const [coverUploadOpen, setCoverUploadOpen] = useState(false);
    const [coverPickerOpen, setCoverPickerOpen] = useState(false);

    // Cover Image Picker Search & Pagination states (limit is 8)
    const [coverPickerSearchVal, setCoverPickerSearchVal] = useState("");
    const [coverPickerSearch, setCoverPickerSearch] = useState("");
    const [coverPickerPage, setCoverPickerPage] = useState(1);
    const [coverPickerTotal, setCoverPickerTotal] = useState(0);
    const [coverPickerHasMore, setCoverPickerHasMore] = useState(false);

    const [newImageName, setNewImageName] = useState("");
    const [newImageFile, setNewImageFile] = useState(null);
    const [newImageUrlInput, setNewImageUrlInput] = useState("");
    const [newImageMode, setNewImageMode] = useState("file"); // 'file' or 'link'
    const [newImageUploading, setNewImageUploading] = useState(false);
    const [uploadingFlightIdx, setUploadingFlightIdx] = useState(null);

    // Drag and drop state
    const [draggedInclusionIndex, setDraggedInclusionIndex] = useState(null);
    const [draggedExclusionIndex, setDraggedExclusionIndex] = useState(null);

    const handleDragStartInclusion = (index) => setDraggedInclusionIndex(index);
    const handleDragEnterInclusion = (index) => {
        if (draggedInclusionIndex === null || draggedInclusionIndex === index) return;
        const newInclusions = [...formData.inclusions];
        const draggedItem = newInclusions[draggedInclusionIndex];
        newInclusions.splice(draggedInclusionIndex, 1);
        newInclusions.splice(index, 0, draggedItem);
        setDraggedInclusionIndex(index);
        handleInputChange(null, "inclusions", newInclusions);
    };
    const handleDragEndInclusion = () => setDraggedInclusionIndex(null);

    const handleDragStartExclusion = (index) => setDraggedExclusionIndex(index);
    const handleDragEnterExclusion = (index) => {
        if (draggedExclusionIndex === null || draggedExclusionIndex === index) return;
        const newExclusions = [...formData.exclusions];
        const draggedItem = newExclusions[draggedExclusionIndex];
        newExclusions.splice(draggedExclusionIndex, 1);
        newExclusions.splice(index, 0, draggedItem);
        setDraggedExclusionIndex(index);
        handleInputChange(null, "exclusions", newExclusions);
    };
    const handleDragEndExclusion = () => setDraggedExclusionIndex(null);

    // Category selection state - pre-populated from request
    const [selectedCategories, setSelectedCategories] = useState([]);

    const [sightSeeing, setSightSeeing] = useState([]);
    const [transports, setTransports] = useState([]);
    const availableCategories = [
        {
            value: "Holiday",
            label: "Holiday Package",
            icon: Calendar,
            requestKey: "is_holiday_package_required",
        },
        {
            value: "Visa",
            label: "Visa",
            icon: FileText,
            requestKey: "is_visa_required",
        },
        {
            value: "Hotel",
            label: "Hotel",
            icon: Hotel,
            requestKey: "is_hotel_booking_required",
        },
        {
            value: "MICE",
            label: "MICE Events",
            icon: Building2,
            requestKey: "is_mice_required",
        },
        {
            value: "Sightseeing",
            label: "Sightseeing",
            icon: ActivityIcon,
            requestKey: "is_sight_seeing_required",
        },
        {
            value: "Transport",
            label: "Transport within City",
            icon: Car,
            requestKey: "is_transport_within_city_required",
        },
        {
            value: "Flight",
            label: "Flight",
            icon: Plane,
            requestKey: "is_transfer_to_destination_required",
            subType: "flight",
        },
        {
            value: "Train",
            label: "Train",
            icon: Train,
            requestKey: "is_transfer_to_destination_required",
            subType: "train",
        },
        {
            value: "Bus",
            label: "Bus",
            icon: Bus,
            requestKey: "is_transfer_to_destination_required",
            subType: "bus",
        },
    ];

    const [formData, setFormData] = useState({
        tripTitle: "",
        city: "",
        bookingRef: "",
        start_date: "",
        end_date: "",
        coverImage: "https://images.unsplash.com/photo-1559827260-dc66d52bef19",
        summary: {
            duration: "",
            travelers: 1,
            adults: 1,
            children: 0,
            infants: 0,
            rating: 4.8,
            highlights: [],
        },
        pricing: {
            airCost: 0,
            visaCost: 0,
            railwayCost: 0,
            packageCost: 0,
            taxes: 0,
            discount: 0,
            total: 0,
            perPerson: 0,
            depositDue: 0,
            currency: "INR",
            tcsPercentage: 2,
        },
        days: [],
        inclusions: [],
        exclusions: [],
        // Category-specific fields
        flights: [],
        visas: [],
        transports_within_city: [],
        mice_events: [],
        standalone_hotels: [],
        sightseeing_packages: [],
        trains: [],
        buses: [],
    });

    useEffect(() => {
        const adults = parseInt(formData.summary.adults) || 1;
        const children = parseInt(formData.summary.children) || 0;
        const infants = parseInt(formData.summary.infants) || 0;
        if (formData.summary.travelers !== adults + children + infants) {
            setFormData(prev => ({
                ...prev,
                summary: {
                    ...prev.summary,
                    travelers: adults + children + infants
                }
            }));
        }
    }, [formData.summary.adults, formData.summary.children, formData.summary.infants]);

    useEffect(() => {
        if (!quotation_id) return;

        const fetchQuotation = async () => {
            try {
                const res = await api.getQuotation(quotation_id);
                const data = res.data.detailed_quotation_data;
                console.log("data", data)
                const available_categories = availableCategories.map(
                    (category) => category.value,
                );
                const not_selected_categories = available_categories.filter(
                    (category) => !data.selected_categories.includes(category),
                );
                const emptyObject = {};
                not_selected_categories.forEach((category) => {
                    if (category === "Flight") {
                        emptyObject.flights = [];
                    } else if (category === "Visa") {
                        emptyObject.visas = [];
                    } else if (category === "Hotel") {
                        emptyObject.standalone_hotels = [];
                    } else if (category === "MICE") {
                        emptyObject.mice_events = [];
                    } else if (category === "Sightseeing") {
                        emptyObject.sightseeing_packages = [];
                    } else if (category === "Transport") {
                        emptyObject.transports_within_city = [];
                    } else if (category === "Train") {
                        emptyObject.trains = [];
                    } else if (category === "Bus") {
                        emptyObject.buses = [];
                    } else if (category === "Holiday") {
                        emptyObject.days = [];
                    }
                });

                if (data.original_currency_data) {
                    setFormData({
                        ...data.original_currency_data,
                        ...emptyObject,
                    });
                    setInputCurrency(data.inputCurrency || "INR");
                    setInputCurrencyRate(data.inputCurrencyRate || 1);
                    setOrientation(data.orientation || "portrait");
                } else {
                    setFormData({
                        ...data,
                        ...emptyObject,
                    });
                    setInputCurrency("INR");
                    setInputCurrencyRate(1);
                    setOrientation(data.orientation || "portrait");
                }

                if (data.original_cost_breakup) {
                    setCostBreakup(data.original_cost_breakup);
                } else {
                    setCostBreakup(res.data.cost_breakup || []);
                }

                setSelectedCategories(
                    res.data.detailed_quotation_data.selected_categories,
                );
                if (res.data.request_id) {
                    setRequestId(res.data.request_id);
                    if (!stateRequest) {
                        try {
                            const reqRes = await api.getRequest(res.data.request_id);
                            setRequest(reqRes.data);
                        } catch (reqErr) {
                            console.error("Error fetching request details:", reqErr);
                        }
                    }
                }
                if (res.data.expiry_date) {
                    const datePart = res.data.expiry_date.split("T")[0];
                    setExpiryDate(datePart);
                }
                loadData();
            } catch (err) {
                console.error(err);
            }
        };

        fetchQuotation();
    }, [quotation_id]);

    useEffect(() => {
        if (inputCurrency && inputCurrency !== "INR" && currencyRates.length > 0) {
            const exists = currencyRates.some(r => r.currency_code === inputCurrency);
            setIsCustomCurrency(!exists);
        }
    }, [inputCurrency, currencyRates]);

    const [newHighlight, setNewHighlight] = useState("");
    const [newInclusion, setNewInclusion] = useState("");
    const [newExclusion, setNewExclusion] = useState("");
    const [showCurrencyDialog, setShowCurrencyDialog] = useState(false);
    const [usdAmount, setUsdAmount] = useState("");
    const AMENITY_OPTIONS = ["wifi", "parking", "pool", "gym", "restaurant"];
    const EQUIPMENT_OPTIONS = [
        "sound_system",
        "microphone",
        "projector",
        "led_screen",
        "lighting",
        "stage",
        "podium",
        "dj_console",
        "video_recording",
        "live_streaming",
        "whiteboard"
    ];

    const loadCoverImagesForPicker = async (page = 1, search = "") => {
        try {
            const res = await api.getCoverImages({
                page: page,
                limit: 6,
                search: search || undefined
            });
            setCoverImagesList(res.data.items || []);
            setCoverPickerTotal(res.data.total || 0);
            setCoverPickerHasMore(res.data.has_more || false);
        } catch (err) {
            console.error("Failed to load cover images for picker:", err);
        }
    };

    useEffect(() => {
        if (coverPickerOpen) {
            loadCoverImagesForPicker(coverPickerPage, coverPickerSearch);
        }
    }, [coverPickerOpen, coverPickerPage, coverPickerSearch]);

    useEffect(() => {
        if (quotation_id) return;
        loadData(true);
    }, []);

    const loadData = async (isInitialLoad = false) => {
        try {
            if (!request) {
                navigate("/dashboard");
                return;
            }
            setLoading(true);

            // Load admin settings
            const settingsResponse = await api.getAdminSettings();
            setAdminSettings(settingsResponse.data);

            // Load catalog
            const catalogResponse = await api.getCatalog();

            setVisas(
                catalogResponse.data.filter((item) => item.category === "visa"),
            );

            setActivities(
                catalogResponse.data.filter(
                    (item) => item.category !== "hotel",
                ),
            );
            setFlights(
                catalogResponse.data.filter(
                    (item) => item.category === "flight",
                ),
            );

            setMiceEvents(
                catalogResponse.data.filter((item) => item.category === "mice"),
            );
            setSightSeeing(
                catalogResponse.data.filter(
                    (item) => item.category === "sightseeing",
                ),
            );
            setTransports(
                catalogResponse.data.filter(
                    (item) => item.category === "transport",
                ),
            );

            setCatalog(catalogResponse.data);
            setHotels(
                catalogResponse.data.filter(
                    (item) => item.category === "hotel",
                ),
            );

            // Load currency rates
            try {
                const currencyResponse = await api.getCurrencyRates();
                setCurrencyRates(currencyResponse.data || []);
            } catch (err) {
                console.error("Error fetching currency rates:", err);
            }

            // Load S3 cover images list
            await loadCoverImagesForPicker(1, "");

            // Pre-fill form data
            const initialData = { ...formData };

            // From admin settings
            if (settingsResponse.data && isInitialLoad) {
                initialData.inclusions =
                    settingsResponse.data.default_inclusions || [];
                initialData.exclusions =
                    settingsResponse.data.default_exclusions || [];
                setDetailedTerms(
                    settingsResponse.data.terms_and_conditions || "",
                );
                setPrivacyPolicy(settingsResponse.data.privacy_policy || "");
                setTestimonials(settingsResponse.data.testimonials || []);
            }

            // From request data
            if (request && isInitialLoad) {
                initialData.city = request.destination || "";
                initialData.summary.travelers = request.people_count || 1;
                initialData.summary.adults = request.adult_count !== undefined ? request.adult_count : 1;
                initialData.summary.children = request.child_count !== undefined ? request.child_count : 0;
                initialData.summary.infants = request.infant_count !== undefined ? request.infant_count : 0;
                initialData.bookingRef = `REF-TVG-${request.id}`;
                initialData.tripTitle = request.title || "";
                initialData.start_date = request.start_date || "";
                initialData.end_date = request.end_date || "";

                // Selected categories
                const preSelectedCategories = [];

                if (request.is_visa_required)
                    preSelectedCategories.push("Visa");
                if (request.is_hotel_booking_required)
                    preSelectedCategories.push("Hotel");
                if (request.is_mice_required)
                    preSelectedCategories.push("MICE");
                if (request.is_sight_seeing_required)
                    preSelectedCategories.push("Sightseeing");
                if (request.is_transport_within_city_required)
                    preSelectedCategories.push("Transport");
                if (request.is_holiday_package_required)
                    preSelectedCategories.push("Holiday");

                if (
                    request.is_transfer_to_destination_required &&
                    request.type_of_travel
                ) {
                    if (request.type_of_travel.includes("flight"))
                        preSelectedCategories.push("Flight");
                    if (request.type_of_travel.includes("train"))
                        preSelectedCategories.push("Train");
                    if (request.type_of_travel.includes("bus"))
                        preSelectedCategories.push("Bus");
                }

                setSelectedCategories(preSelectedCategories);
            }

            if (isInitialLoad) {
                setFormData(initialData);
            }
        } catch (error) {
            console.error("Error loading data:", error);
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const filteredActivities = useMemo(() => {
        let filtered = activities;
        if (activityFilter !== "All") {
            filtered = filtered.filter(item => item.category && item.category.toLowerCase() === activityFilter.toLowerCase());
        }
        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            filtered = filtered.filter((item) =>
                item.title.toLowerCase().includes(lowerSearchTerm) ||
                (item.destination || "").toLowerCase().includes(lowerSearchTerm)
            );
        }
        return filtered;
    }, [searchTerm, activityFilter, activities]);

    const filteredFlights = useMemo(() => {
        if (!searchTerm) return flights;
        return flights.filter((item) =>
            item.title.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [searchTerm, flights]);

    const filteredHotels = useMemo(() => {
        if (!searchTerm) return hotels;
        return hotels.filter((item) =>
            item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.destination || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item?.base_data_json?.city || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item?.base_data_json?.address || "").toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, hotels]);

    const filteredMiceEvents = useMemo(() => {
        if (!searchTerm) return miceEvents;
        return miceEvents.filter((item) =>
            item.title.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [searchTerm, miceEvents]);

    const filteredSightSeeing = useMemo(() => {
        if (!searchTerm) return sightSeeing;
        return sightSeeing.filter((item) =>
            item.title.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [searchTerm, sightSeeing]);

    const filteredTransports = useMemo(() => {
        if (!searchTerm) return transports;
        return transports.filter((item) =>
            item.title.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [searchTerm, transports]);

    const filteredVisas = useMemo(() => {
        if (!searchTerm) return visas;
        return visas.filter((item) =>
            item.title.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [searchTerm, visas]);

    const [inrAmount, setInrAmount] = useState("");
    const [conversionRate, setConversionRate] = useState(83); // default 83

    const handleInputChange = (section, field, value) => {
        if (section) {
            setFormData((prev) => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value,
                },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [field]: value,
            }));
        }
    };

    const toggleCategory = (category) => {
        setSelectedCategories((prev) => {
            if (prev.includes(category)) {
                return prev.filter((c) => c !== category);
            } else {
                return [...prev, category];
            }
        });
    };

    const updateTrainField = (trainIdx, field, value) => {
        const updated = [...formData.trains];
        updated[trainIdx][field] = value;

        if (
            field === "cost_per_person" ||
            field === "total_passengers" ||
            field === "gst_percentage"
        ) {
            const train = updated[trainIdx];

            const cost = train.cost_per_person || 0;
            const passengers = train.total_passengers || 0;
            const gst = train.gst_percentage || 0;

            const baseTotal = cost * passengers;
            const totalWithGst = baseTotal * (1 + gst / 100);

            train.total_cost = Math.round(totalWithGst);
        }

        setFormData((prev) => ({
            ...prev,
            trains: updated,
        }));
    };

    const updateFlightField = (flightIdx, field, value) => {
        const updated = [...formData.flights]; // FIXED
        const flight = updated[flightIdx];

        flight[field] = value;

        // Recalculate total when relevant fields change
        if (
            field === "cost_per_person" ||
            field === "total_passengers" ||
            field === "gst_percentage"
        ) {
            const cost = flight.cost_per_person || 0;
            const passengers = flight.total_passengers || 0;
            const gst = flight.gst_percentage || 0;

            const baseTotal = cost * passengers;
            const totalWithGst = baseTotal * (1 + gst / 100);

            flight.total_cost = Math.round(totalWithGst);
        }

        setFormData((prev) => ({
            ...prev,
            flights: updated,
        }));
    };

    // Flight management
    const handleFlightScreenshotUpload = async (flightIdx, file) => {
        if (!file) return;
        try {
            setUploadingFlightIdx(flightIdx);
            const res = await api.uploadFile(file);
            if (res.data && (res.data.file_url || res.data.url)) {
                const uploadedUrl = res.data.file_url || res.data.url;
                updateFlightField(flightIdx, "screenshot", uploadedUrl);
                toast.success("Flight screenshot uploaded successfully!");
            }
        } catch (err) {
            console.error("Flight screenshot upload error:", err);
            toast.error("Failed to upload screenshot. Please try again or paste image URL.");
        } finally {
            setUploadingFlightIdx(null);
        }
    };

    const addFlight = () => {
        const newFlight = {
            id: uuid(),
            booking_reference: "",
            total_passengers: formData.summary.travelers,
            journey_type: "Round-trip",
            total_cost: 0,
            cost_per_person: 0,
            gst_percentage: 0,
            input_mode: "screenshot",
            screenshot: "",
            segments: [],
            notes: "",
        };
        setFormData((prev) => ({
            ...prev,
            flights: [...prev.flights, newFlight],
        }));
    };

    const addFlightSegmentIfFlightCatalogExist = (item) => {
        const newFlight = {
            id: uuid(),
            booking_reference: item.base_data_json.booking_reference || "",
            total_passengers: formData.summary.travelers,
            journey_type: item.base_data_json.journey_type,
            total_cost: item.default_price || 0,
            cost_per_person: item.default_price || 0,
            gst_percentage: 0,
            segments: item.base_data_json.segments,
            notes: item.description || "",
        };
        setFormData((prev) => ({
            ...prev,
            flights: [...prev.flights, newFlight],
        }));
    };

    const addFlightSegment = (flightIndex) => {
        const newSegment = {
            id: uuid(),
            flight_number: "",
            airline: "",
            airline_logo: "",
            departure_airport: "",
            departure_city: "",
            departure_time: "",
            departure_date: "",
            arrival_airport: "",
            arrival_city: "",
            arrival_time: "",
            arrival_date: "",
            duration: "",
            cabin_class: "Economy",
            baggage_checkin: "15 KG",
            baggage_cabin: "7 KG",
        };
        setFormData((prev) => ({
            ...prev,
            flights: prev.flights.map((flight, idx) =>
                idx === flightIndex
                    ? { ...flight, segments: [...flight.segments, newSegment] }
                    : flight,
            ),
        }));
    };

    const updateFlightSegment = (flightIndex, segmentIndex, field, value) => {
        setFormData((prev) => ({
            ...prev,
            flights: prev.flights.map((flight, fIdx) =>
                fIdx === flightIndex
                    ? {
                        ...flight,
                        segments: flight.segments.map((segment, sIdx) =>
                            sIdx === segmentIndex
                                ? { ...segment, [field]: value }
                                : segment,
                        ),
                    }
                    : flight,
            ),
        }));
    };

    const removeFlightSegment = (flightIndex, segmentIndex) => {
        setFormData((prev) => ({
            ...prev,
            flights: prev.flights.map((flight, fIdx) =>
                fIdx === flightIndex
                    ? {
                        ...flight,
                        segments: flight.segments.filter(
                            (_, sIdx) => sIdx !== segmentIndex,
                        ),
                    }
                    : flight,
            ),
        }));
    };

    const removeFlight = (flightIndex) => {
        setFormData((prev) => ({
            ...prev,
            flights: prev.flights.filter((_, idx) => idx !== flightIndex),
        }));
    };

    // Visa management
    const addVisaIfExistCatalog = (item) => {
        const newVisa = {
            id: uuid(),
            name: item.title || "",
            visa_type: item.visa_type || "Tourist",
            destination_country: item.destination || "",
            processing_time_days: item.base_data_json.processing_time_days || 5,
            cost_per_person: item.base_data_json.default_price || 0,
            number_of_people: formData.summary.travelers,
            total_cost: item.base_data_json.default_price || 0,
            gst_percentage: 0,
            description: item.description || "",
        };
        setFormData((prev) => ({
            ...prev,
            visas: [...prev.visas, newVisa],
        }));
    };

    const addVisa = () => {
        const newVisa = {
            id: uuid(),
            name: "",
            visa_type: "Tourist",
            destination_country: "",
            processing_time_days: 5,
            cost_per_person: 0,
            number_of_people: formData.summary.travelers,
            total_cost: 0,
            gst_percentage: 0,
            description: "",
        };
        setFormData((prev) => ({
            ...prev,
            visas: [...prev.visas, newVisa],
        }));
    };

    const updateBusSegment = (busIdx, segIdx, field, value) => {
        setFormData((prev) => ({
            ...prev,
            buses: prev.buses.map((bus, bIndex) =>
                bIndex === busIdx
                    ? {
                        ...bus,
                        segments: bus.segments.map((segment, sIndex) =>
                            sIndex === segIdx
                                ? { ...segment, [field]: value }
                                : segment,
                        ),
                    }
                    : bus,
            ),
        }));
    };

    const updateVisa = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            visas: prev.visas.map((visa, idx) => {
                if (idx === index) {
                    const updated = { ...visa, [field]: value };
                    // Auto-calculate total_cost
                    if (
                        field === "cost_per_person" ||
                        field === "number_of_people" ||
                        field === "gst_percentage"
                    ) {
                        updated.total_cost =
                            Math.round((updated.cost_per_person *
                                updated.number_of_people *
                                (100 + updated.gst_percentage)) /
                                100);
                    }
                    return updated;
                }
                return visa;
            }),
        }));
    };

    const removeVisa = (index) => {
        setFormData((prev) => ({
            ...prev,
            visas: prev.visas.filter((_, idx) => idx !== index),
        }));
    };

    // Transport management
    const addTransport = () => {
        const newTransport = {
            id: uuid(),
            vehicle_type: "Sedan",
            vehicle_name: "",
            capacity: 4,
            pickup_location: "",
            drop_location: "",
            pickup_date: "",
            pickup_time: "",
            duration: "",
            total_cost: 0,
            cost_per_vehicle: 0,
            number_of_vehicles: 1,
            gst_percentage: 10,
            driver_details: "",
            notes: "",
        };
        setFormData((prev) => ({
            ...prev,
            transports_within_city: [
                ...prev.transports_within_city,
                newTransport,
            ],
        }));
    };

    const addTransportForCatalogIfExist = (item) => {
        const newTransport = {
            id: uuid(),
            vehicle_type: item.base_data_json.vehicle_type || "Sedan",
            vehicle_name: item.title || "",
            capacity: item.base_data_json.capacity || 4,
            pickup_location: item.base_data_json.pickup_location || "",
            drop_location: item.base_data_json.drop_location || "",
            pickup_date: item.base_data_json.pickup_date || "",
            pickup_time: item.base_data_json.pickup_time || "",
            duration: item.base_data_json.duration || "",
            total_cost: 0,
            cost_per_vehicle: item.base_data_json.default_price || 0,
            number_of_vehicles: item.base_data_json.number_of_vehicles || 1,
            gst_percentage: 10,
            driver_details: item.base_data_json.driver_details || "",
            notes: item.description || "",
        };
        setFormData((prev) => ({
            ...prev,
            transports_within_city: [
                ...prev.transports_within_city,
                newTransport,
            ],
        }));
    };
    const updateTransport = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            transports_within_city: prev.transports_within_city.map(
                (transport, idx) => {
                    if (idx === index) {
                        const updated = { ...transport, [field]: value };
                        // Auto-calculate total_cost
                        if (
                            field === "cost_per_vehicle" ||
                            field === "number_of_vehicles" ||
                            field === "gst_percentage"
                        ) {
                            updated.total_cost =
                                Math.round(updated.cost_per_vehicle *
                                    updated.number_of_vehicles *
                                    (1 + updated.gst_percentage / 100));
                        }
                        return updated;
                    }
                    return transport;
                },
            ),
        }));
    };

    const removeTransport = (index) => {
        setFormData((prev) => ({
            ...prev,
            transports_within_city: prev.transports_within_city.filter(
                (_, idx) => idx !== index,
            ),
        }));
    };

    // MICE Events management
    const addMiceEvent = () => {
        const newEvent = {
            id: uuid(),
            event_type: "Meeting",
            event_name: "",
            venue_name: "",
            venue_address: "",
            capacity: 0,
            number_of_attendees: formData.summary.travelers,
            event_date: "",
            event_time: "",
            duration: "",
            equipment_provided: [],
            catering_included: false,
            catering_details: "",
            total_cost: 0,
            cost_per_person: 0,
            gst_percentage: 0,
        };
        setFormData((prev) => ({
            ...prev,
            mice_events: [...prev.mice_events, newEvent],
        }));
    };

    const updateMiceEvent = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            mice_events: prev.mice_events.map((event, idx) => {
                if (idx === index) {
                    const updated = { ...event, [field]: value };
                    // Auto-calculate total_cost
                    if (
                        field === "cost_per_person" ||
                        field === "number_of_attendees" ||
                        field === "gst_percentage"
                    ) {
                        const gst = updated.gst_percentage || 0;
                        updated.total_cost =
                            Math.round(updated.cost_per_person *
                                updated.number_of_attendees *
                                (1 + gst / 100));
                    }
                    return updated;
                }
                return event;
            }),
        }));
    };

    const removeMiceEvent = (index) => {
        setFormData((prev) => ({
            ...prev,
            mice_events: prev.mice_events.filter((_, idx) => idx !== index),
        }));
    };

    // Standalone Hotels management
    const addStandaloneHotel = () => {
        const newHotel = {
            id: uuid(),
            name: "",
            stars: 3,
            image: "",
            address: "",
            city: formData.city,
            check_in_date: "",
            check_out_date: "",
            number_of_nights: 1,
            room_type: "Standard",
            number_of_rooms: 1,
            guests_per_room: 2,
            meal_plan: "EP (Room Only)",
            url: "",
            amenities: [],
            total_cost: 0,
            cost_per_room_per_night: 0,
            gst_percentage: 18,
            notes: "",
        };
        setFormData((prev) => ({
            ...prev,
            standalone_hotels: [...prev.standalone_hotels, newHotel],
        }));
    };

    const addStandaloneHotelIfCatalogExistForHotel = (item) => {
        const newHotel = {
            id: uuid(),
            name: item.title,
            stars: item.base_data_json.stars || 3,
            image: item.base_data_json.image_url || "",
            address: item.destination || "",
            city: formData.city,
            check_in_date: item.base_data_json.check_in_date,
            check_out_date: item.base_data_json.check_out_date,
            number_of_nights: item.base_data_json.number_of_nights,
            room_type: item.base_data_json.room_type,
            number_of_rooms: item.base_data_json.number_of_rooms,
            guests_per_room: item.base_data_json.guests_per_room,
            meal_plan: item.base_data_json.meal_plan,
            url: item.base_data_json.image_url || "",
            amenities: (item.base_data_json.amenities || "").trim() ? item.base_data_json.amenities.split(",") : [],
            total_cost: item.base_data_json.default_price || 0,
            cost_per_room_per_night: item.base_data_json.default_price || 0,
            gst_percentage: 18,
            notes: item.description || "",
        };
        setFormData((prev) => ({
            ...prev,
            standalone_hotels: [...prev.standalone_hotels, newHotel],
        }));
    };

    const addMiceEventForCatalogIfExist = (item) => {
        const newEvent = {
            id: uuid(),
            event_type: "Meeting",
            event_name: item.title,
            venue_name: item.base_data_json.venue_name,
            venue_address: item.base_data_json.venue_address || "",
            capacity: item.base_data_json.capacity,
            number_of_attendees: formData.summary.travelers,
            event_date: item.base_data_json.event_date,
            event_time: item.base_data_json.event_time,
            duration: item.base_data_json.duration,
            equipment_provided: item.base_data_json.equipment_provided,
            catering_included: false,
            catering_details: item.base_data_json.catering_details,
            total_cost: item.base_data_json.total_cost,
            cost_per_person: item.base_data_json.default_price || 0,
            gst_percentage: 0,
        };
        setFormData((prev) => ({
            ...prev,
            mice_events: [...prev.mice_events, newEvent],
        }));
    };

    const updateStandaloneHotel = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            standalone_hotels: prev.standalone_hotels.map((hotel, idx) => {
                if (idx === index) {
                    const updated = { ...hotel, [field]: value };

                    // Auto-calculate nights if dates change
                    if (
                        field === "check_in_date" ||
                        field === "check_out_date"
                    ) {
                        if (updated.check_in_date && updated.check_out_date) {
                            const checkIn = new Date(updated.check_in_date);
                            const checkOut = new Date(updated.check_out_date);
                            updated.number_of_nights = Math.ceil(
                                (checkOut - checkIn) / (1000 * 60 * 60 * 24),
                            );
                        }
                    }

                    // Auto-calculate total_cost
                    if (
                        field === "cost_per_room_per_night" ||
                        field === "number_of_rooms" ||
                        field === "number_of_nights" ||
                        field === "guests_per_room" ||
                        field === "gst_percentage" ||
                        field === "check_in_date" ||
                        field === "check_out_date"
                    ) {
                        const gst = updated.gst_percentage || 0;
                        if (updated.cost_per_room_per_night && updated.number_of_rooms && updated.number_of_nights) {
                            updated.total_cost =
                                Math.round(updated.cost_per_room_per_night *
                                    updated.number_of_rooms *
                                    updated.number_of_nights *
                                    (1 + gst / 100));
                        } else {
                            updated.total_cost = 0;
                        }
                    }
                    return updated;
                }
                return hotel;
            }),
        }));
    };

    const removeStandaloneHotel = (index) => {
        setFormData((prev) => ({
            ...prev,
            standalone_hotels: prev.standalone_hotels.filter(
                (_, idx) => idx !== index,
            ),
        }));
    };

    // Sightseeing Packages management
    const addSightseeingPackageIfExistCatalog = (item) => {
        const newPackage = {
            id: uuid(),
            package_name: item.title || "",
            city: formData.city,
            date: item.base_data_json.event_date,
            start_time: item.base_data_json.start_time,
            end_time: item.base_data_json.end_time,
            duration: item.base_data_json.duration,
            places: item.base_data_json.places,
            transport_included: item.base_data_json.transport_included,
            transport_details: item.base_data_json.transport_details,
            guide_included: item.base_data_json.guide_included,
            meal_included: item.base_data_json.meal_included,
            meal_details: item.base_data_json.meal_details,
            total_cost: 0,
            cost_per_person: item.base_data_json.default_price || 0,
            number_of_people: formData.summary.travelers,
            gst_percentage: 5,
            notes: item.description || "",
        };
        setFormData((prev) => ({
            ...prev,
            sightseeing_packages: [...prev.sightseeing_packages, newPackage],
        }));
    };

    const addSightseeingPackage = () => {
        const newPackage = {
            id: uuid(),
            package_name: "",
            city: formData.city,
            date: "",
            start_time: "",
            end_time: "",
            duration: "Full Day",
            places: [],
            transport_included: true,
            transport_details: "",
            guide_included: true,
            meal_included: false,
            meal_details: "",
            total_cost: 0,
            cost_per_person: 0,
            number_of_people: formData.summary.travelers,
            gst_percentage: 5,
            notes: "",
        };
        setFormData((prev) => ({
            ...prev,
            sightseeing_packages: [...prev.sightseeing_packages, newPackage],
        }));
    };

    const addPlaceToPackage = (packageIndex) => {
        const newPlace = {
            name: "",
            description: "",
            image: "",
            duration: "",
            entry_fee_included: true,
        };
        setFormData((prev) => ({
            ...prev,
            sightseeing_packages: prev.sightseeing_packages.map((pkg, idx) =>
                idx === packageIndex
                    ? { ...pkg, places: [...pkg.places, newPlace] }
                    : pkg,
            ),
        }));
    };

    const updatePlaceInPackage = (packageIndex, placeIndex, field, value) => {
        setFormData((prev) => ({
            ...prev,
            sightseeing_packages: prev.sightseeing_packages.map((pkg, pIdx) =>
                pIdx === packageIndex
                    ? {
                        ...pkg,
                        places: pkg.places.map((place, plIdx) =>
                            plIdx === placeIndex
                                ? { ...place, [field]: value }
                                : place,
                        ),
                    }
                    : pkg,
            ),
        }));
    };

    const removePlaceFromPackage = (packageIndex, placeIndex) => {
        setFormData((prev) => ({
            ...prev,
            sightseeing_packages: prev.sightseeing_packages.map((pkg, pIdx) =>
                pIdx === packageIndex
                    ? {
                        ...pkg,
                        places: pkg.places.filter(
                            (_, plIdx) => plIdx !== placeIndex,
                        ),
                    }
                    : pkg,
            ),
        }));
    };

    const updateSightseeingPackage = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            sightseeing_packages: prev.sightseeing_packages.map((pkg, idx) => {
                if (idx === index) {
                    const updated = { ...pkg, [field]: value };
                    // Auto-calculate total_cost
                    if (
                        field === "cost_per_person" ||
                        field === "number_of_people" ||
                        field === "gst_percentage"
                    ) {
                        const gst = updated.gst_percentage || 0;
                        updated.total_cost =
                            Math.round(updated.cost_per_person *
                                updated.number_of_people *
                                (1 + gst / 100));
                    }
                    return updated;
                }
                return pkg;
            }),
        }));
    };

    const removeSightseeingPackage = (index) => {
        setFormData((prev) => ({
            ...prev,
            sightseeing_packages: prev.sightseeing_packages.filter(
                (_, idx) => idx !== index,
            ),
        }));
    };

    // Train management
    const addTrain = () => {
        const newTrain = {
            id: uuid(),
            pnr: "",
            total_passengers: formData.summary.travelers,
            total_cost: 0,
            cost_per_person: 0,
            gst_percentage: 0,
            segments: [],
            notes: "",
        };
        setFormData((prev) => ({
            ...prev,
            trains: [...prev.trains, newTrain],
        }));
    };

    const addTrainSegment = (trainIndex) => {
        const newSegment = {
            train_number: "",
            train_name: "",
            departure_station: "",
            departure_city: "",
            departure_time: "",
            departure_date: "",
            arrival_station: "",
            arrival_city: "",
            arrival_time: "",
            arrival_date: "",
            duration: "",
            class_type: "AC 3 Tier",
            seat_numbers: "",
        };
        setFormData((prev) => ({
            ...prev,
            trains: prev.trains.map((train, idx) =>
                idx === trainIndex
                    ? { ...train, segments: [...train.segments, newSegment] }
                    : train,
            ),
        }));
    };

    const removeTrain = (trainIndex) => {
        setFormData((prev) => ({
            ...prev,
            trains: prev.trains.filter((_, idx) => idx !== trainIndex),
        }));
    };

    // Bus management
    const addBus = () => {
        const newBus = {
            id: uuid(),
            total_passengers: formData.summary.travelers,
            total_cost: 0,
            gst_percentage: 0,
            segments: [],
            notes: "",
        };
        setFormData((prev) => ({
            ...prev,
            buses: [...prev.buses, newBus],
        }));
    };

    const addBusSegment = (busIndex) => {
        const newSegment = {
            bus_operator: "",
            bus_type: "AC Sleeper",
            departure_location: "",
            departure_time: "",
            departure_date: "",
            duration: "",
            arrival_location: "",
            arrival_time: "",
            arrival_date: "",
        };
        setFormData((prev) => ({
            ...prev,
            buses: prev.buses.map((bus, idx) =>
                idx === busIndex
                    ? { ...bus, segments: [...bus.segments, newSegment] }
                    : bus,
            ),
        }));
    };

    const removeBus = (busIndex) => {
        setFormData((prev) => ({
            ...prev,
            buses: prev.buses.filter((_, idx) => idx !== busIndex),
        }));
    };

    // Highlights
    const addHighlight = () => {
        if (newHighlight.trim()) {
            const newHighlights = newHighlight.trim().split("&&")
            const filteresNewHighlights = newHighlights.filter((highlight) => highlight.trim())
            setFormData((prev) => ({
                ...prev,
                summary: {
                    ...prev.summary,
                    highlights: [
                        ...prev.summary.highlights,
                        ...filteresNewHighlights,
                    ],
                },
            }));
            setNewHighlight("");
        }
    };

    const removeHighlight = (index) => {
        setFormData((prev) => ({
            ...prev,
            summary: {
                ...prev.summary,
                highlights: prev.summary.highlights.filter(
                    (_, i) => i !== index,
                ),
            },
        }));
    };

    // Inclusions/Exclusions
    const addInclusion = () => {
        if (newInclusion.trim()) {
            setFormData((prev) => ({
                ...prev,
                inclusions: [...prev.inclusions, newInclusion.trim()],
            }));
            setNewInclusion("");
        }
    };

    const removeInclusion = (index) => {
        setFormData((prev) => ({
            ...prev,
            inclusions: prev.inclusions.filter((_, i) => i !== index),
        }));
    };

    const addExclusion = () => {
        if (newExclusion.trim()) {
            setFormData((prev) => ({
                ...prev,
                exclusions: [...prev.exclusions, newExclusion.trim()],
            }));
            setNewExclusion("");
        }
    };

    const removeExclusion = (index) => {
        setFormData((prev) => ({
            ...prev,
            exclusions: prev.exclusions.filter((_, i) => i !== index),
        }));
    };

    // Days management
    const generateDays = (dayCount) => {
        const days = Array.from({ length: dayCount }, (_, idx) => ({
            dayNumber: idx + 1,
            date: new Date(
                new Date(formData.start_date).getTime() +
                idx * 24 * 60 * 60 * 1000,
            )
                .toISOString()
                .split("T")[0],
            location: "",
            meals: {
                breakfast: "Not Included",
                lunch: "Not Included",
                dinner: "Not Included",
            },
            hotel: null,
            activities: [],
        }));

        return days;
    };

    const removeDay = (index) => {
        setFormData((prev) => ({
            ...prev,
            days: prev.days
                .filter((_, i) => i !== index)
                .map((day, idx) => ({
                    ...day,
                    dayNumber: idx + 1,
                })),
        }));
    };

    const updateDay = (index, field, value) => {
        setFormData((prev) => {
            const updatedDays = [...prev.days];
            const day = { ...updatedDays[index] };

            // 1️⃣ Remove hotel cost from costBreakup if hotel changed
            if (field === "hotel" && day.hotel?.id) {
                setCostBreakup((prevCost) =>
                    prevCost.filter((item) => item.id !== day.hotel.id),
                );
            }

            // 2️⃣ Update the changed field first
            day[field] = value;

            // 3️⃣ Recalculate total when needed
            if (
                field === "cost_per_person" ||
                field === "total_passengers" ||
                field === "gst_percentage"
            ) {
                const cost = Number(day.cost_per_person) || 0;
                const passengers = Number(day.total_passengers) || 0;
                const gst = Number(day.gst_percentage) || 0;

                const baseTotal = cost * passengers;
                const totalWithGst = baseTotal * (1 + gst / 100);

                day.total_cost = Math.round(totalWithGst);
            }

            updatedDays[index] = day;

            return {
                ...prev,
                days: updatedDays,
            };
        });
    };

    const addActivityToDay = (dayIndex, activity) => {
        setFormData((prev) => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex
                    ? { ...day, activities: [...day.activities, activity] }
                    : day,
            ),
        }));
    };

    const removeActivityFromDay = (dayIndex, activityIndex) => {
        setCostBreakup((prev) =>
            prev.filter(
                (item) =>
                    item.id !==
                    formData.days[dayIndex].activities[activityIndex].id,
            ),
        );
        setFormData((prev) => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex
                    ? {
                        ...day,
                        activities: day.activities.filter(
                            (_, ai) => ai !== activityIndex,
                        ),
                    }
                    : day,
            ),
        }));
    };

    const openCreateCatalogModal = (category) => {
        setCreateCatalogCategory(category);
        setShowCreateCatalogModal(true);
    };

    const handleCatalogSaved = (newItem) => {
        setCatalog(prev => [...prev, newItem]);
        if (newItem.category === "hotel") {
            setHotels(prev => [...prev, newItem]);
        } else if (newItem.category === "flight") {
            setFlights(prev => [...prev, newItem]);
        } else if (newItem.category === "visa") {
            setVisas(prev => [...prev, newItem]);
        } else if (newItem.category === "mice") {
            setMiceEvents(prev => [...prev, newItem]);
        } else if (newItem.category === "sightseeing") {
            setSightSeeing(prev => [...prev, newItem]);
        } else if (newItem.category === "transport") {
            setTransports(prev => [...prev, newItem]);
        }

        if (newItem.category !== "hotel") {
            setActivities(prev => [...prev, newItem]);
        }
    };

    const addActivityFromCatalog = (dayIndex, catalogItem) => {
        const id = uuid();
        const activity = {
            id,
            time: catalogItem?.base_data_json?.time || "",
            title: catalogItem?.title || "",
            description: catalogItem?.description || "",
            image: catalogItem?.base_data_json?.image_url,
            meetingPoint: catalogItem?.destination || "",
            type: "included",
        };
        const activityCostBreakup = {
            id,
            name: catalogItem.title,
            date: formData.days[dayIndex].date,
            quantity: 1,
            unit_cost: catalogItem?.base_data_json?.default_price || 0,
        };
        setCostBreakup((prev) => [...prev, activityCostBreakup]);
        addActivityToDay(dayIndex, activity);
    };

    const addHotelFromCatalog = (dayIndex, catalogItem) => {
        const id = uuid();
        const hotel = {
            id,
            name: catalogItem.title,
            stars: catalogItem.base_data_json.stars || 0,
            image: catalogItem.base_data_json.image_url || "",
            address: catalogItem.base_data_json.address || "",
            amenities: (catalogItem.base_data_json.amenities || "")
                .split(",")
                .map((a) => a.trim()),
        };
        const hotelCostBreakup = {
            id,
            name: catalogItem.title,
            date: formData.days[dayIndex].date,
            quantity: 1,
            unit_cost: catalogItem.base_data_json.price || 0,
        };
        setCostBreakup((prev) => [...prev, hotelCostBreakup]);
        setFormData((prev) => ({
            ...prev,
            days: prev.days.map((day, i) =>
                i === dayIndex ? { ...day, hotel } : day,
            ),
        }));
    };

    const validateQuotation = () => {
        if (!formData.tripTitle?.trim()) {
            toast.error("Trip title is required");
            return false;
        }

        if (!formData.city?.trim()) {
            toast.error("Destination is required");
            return false;
        }

        if (!formData.start_date || !formData.end_date) {
            toast.error("Start and end dates are required");
            return false;
        }

        if (new Date(formData.start_date) > new Date(formData.end_date)) {
            toast.error("End date cannot be before start date");
            return false;
        }

        if (formData.summary.travelers < 1) {
            toast.error("At least 1 traveler is required");
            return false;
        }

        if (selectedCategories.length === 0) {
            toast.error("Please select at least one category");
            return false;
        }

        return true;
    };

    useEffect(() => {
        setFormData((prev) => {
            const sumCategory = (arr = []) =>
                arr.reduce((t, item) => t + (Number(item.total_cost) || 0), 0);

            const sumCostBreakup = (arr = []) =>
                arr.reduce(
                    (t, item) =>
                        t +
                        (Number(item.unit_cost) || 0) *
                        (Number(item.quantity) || 0),
                    0,
                );

            const categoryMap = {
                Visa: prev.visas,
                Flight: prev.flights,
                Hotel: prev.standalone_hotels,
                MICE: prev.mice_events,
                Sightseeing: prev.sightseeing_packages,
                Transport: prev.transports_within_city,
                Train: prev.trains,
                Bus: prev.buses,
                Holiday: prev.days,
            };

            let airCost = 0;
            let visaCost = 0;
            let railwayCost = 0;
            let packageCost = 0;

            (selectedCategories || []).forEach(category => {
                if (!categoryMap[category]) return;

                let catSum = sumCategory(categoryMap[category]);
                if (category === "Holiday") {
                    catSum += sumCostBreakup(costBreakup);
                }

                if (category === "Flight") {
                    airCost += catSum;
                } else if (category === "Visa") {
                    visaCost += catSum;
                } else if (category === "Train") {
                    railwayCost += catSum;
                } else {
                    packageCost += catSum;
                }
            });

            const total = airCost + visaCost + railwayCost + packageCost;
            const travelers = Number(prev.summary.travelers) || 1;

            // 🔥 Prevent unnecessary state update
            if (
                prev.pricing.airCost === airCost &&
                prev.pricing.visaCost === visaCost &&
                prev.pricing.railwayCost === railwayCost &&
                prev.pricing.packageCost === packageCost &&
                prev.pricing.total === total &&
                prev.pricing.perPerson === total / travelers
            ) {
                return prev;
            }

            return {
                ...prev,
                pricing: {
                    ...prev.pricing,
                    airCost,
                    visaCost,
                    railwayCost,
                    packageCost,
                    total,
                    perPerson: total / travelers,
                },
            };
        });
    }, [
        costBreakup,
        formData.visas,
        formData.flights,
        formData.standalone_hotels,
        formData.mice_events,
        formData.sightseeing_packages,
        formData.transports_within_city,
        formData.trains,
        formData.buses,
        formData.days,
        selectedCategories,
        formData.pricing.discount,
        formData.summary.travelers,
    ]);

    const handleSave = async (status = "DRAFT", expiry_date, bypassAdvanceCheck = false) => {
        try {
            if (!validateQuotation() && status === "SENT") return;

            const deposit = Number(formData.pricing.depositDue) || 0;
            if (status === "SENT" && deposit === 0 && !bypassAdvanceCheck) {
                setPendingSaveParams({ status, expiry_date });
                setShowAdvanceWarning(true);
                return;
            }

            const dayCount =
                (new Date(formData.end_date) - new Date(formData.start_date)) /
                (1000 * 60 * 60 * 24);
            const durationText = `${dayCount + 1} Days ${dayCount} Nights`;
            const finalRequestId = requestId || request?.id;

            const detailed_quotation_data = JSON.parse(
                JSON.stringify(formData),
            );
            detailed_quotation_data.summary.duration = durationText;
            detailed_quotation_data.selected_categories = selectedCategories;

            if (!detailed_quotation_data.selected_categories.includes("Visa")) {
                delete detailed_quotation_data.visas;
            }

            if (
                !detailed_quotation_data.selected_categories.includes("Flight")
            ) {
                delete detailed_quotation_data.flights;
            }

            if (
                !detailed_quotation_data.selected_categories.includes("Hotel")
            ) {
                delete detailed_quotation_data.standalone_hotels;
            }

            if (!detailed_quotation_data.selected_categories.includes("MICE")) {
                delete detailed_quotation_data.mice_events;
            }

            if (
                !detailed_quotation_data.selected_categories.includes(
                    "Sightseeing",
                )
            ) {
                delete detailed_quotation_data.sightseeing_packages;
            }

            if (
                !detailed_quotation_data.selected_categories.includes(
                    "Transport",
                )
            ) {
                delete detailed_quotation_data.transports_within_city;
            }

            if (
                !detailed_quotation_data.selected_categories.includes("Train")
            ) {
                delete detailed_quotation_data.trains;
            }

            if (!detailed_quotation_data.selected_categories.includes("Bus")) {
                delete detailed_quotation_data.buses;
            }

            if (
                !detailed_quotation_data.selected_categories.includes("Holiday")
            ) {
                delete detailed_quotation_data.days;
            }

            const detailed_quotation_data_inr = convertQuotationDataToINR(detailed_quotation_data, inputCurrencyRate);
            detailed_quotation_data_inr.original_currency_data = formData;
            detailed_quotation_data_inr.original_cost_breakup = costBreakup;
            detailed_quotation_data_inr.inputCurrency = inputCurrency;
            detailed_quotation_data_inr.inputCurrencyRate = inputCurrencyRate;
            detailed_quotation_data_inr.orientation = orientation;

            const cost_breakup_inr = costBreakup.map(item => ({
                ...item,
                unit_cost: Math.ceil((Number(item.unit_cost) || 0) * inputCurrencyRate),
            }));

            const payload = {
                request_id: finalRequestId,
                status: status || "DRAFT",
                detailed_quotation_data: detailed_quotation_data_inr,
                expiry_date: expiry_date || null,
                cost_breakup: cost_breakup_inr,
            };
            setSaving(true);
            if (quotation_id) {
                await api.updateQuotation(quotation_id, payload);
                toast.success("Quotation saved successfully!");
                navigate(`/requests/${finalRequestId}`);
                return;
            }

            await api.createQuotation(payload);
            toast.success("Quotation saved successfully!");
            navigate(`/requests/${finalRequestId}`);
        } catch (error) {
            console.error("Error saving quotation:", error);
            toast.error("Failed to save quotation");
        } finally {
            setSaving(false);
        }
    };

    const handlePreview = async () => {
        try {
            setPreviewing(true);
            const dayCount =
                (new Date(formData.end_date) - new Date(formData.start_date)) /
                (1000 * 60 * 60 * 24);
            const durationText = `${dayCount + 1} Days ${dayCount} Nights`;

            const detailed_quotation_data = JSON.parse(
                JSON.stringify(formData),
            );
            detailed_quotation_data.summary.duration = durationText;
            detailed_quotation_data.selected_categories = selectedCategories;

            if (!detailed_quotation_data.selected_categories.includes("Visa")) {
                delete detailed_quotation_data.visas;
            }
            if (!detailed_quotation_data.selected_categories.includes("Flight")) {
                delete detailed_quotation_data.flights;
            }
            if (!detailed_quotation_data.selected_categories.includes("Hotel")) {
                delete detailed_quotation_data.standalone_hotels;
            }
            if (!detailed_quotation_data.selected_categories.includes("MICE")) {
                delete detailed_quotation_data.mice_events;
            }
            if (!detailed_quotation_data.selected_categories.includes("Sightseeing")) {
                delete detailed_quotation_data.sightseeing_packages;
            }
            if (!detailed_quotation_data.selected_categories.includes("Transport")) {
                delete detailed_quotation_data.transports_within_city;
            }
            if (!detailed_quotation_data.selected_categories.includes("Train")) {
                delete detailed_quotation_data.trains;
            }
            if (!detailed_quotation_data.selected_categories.includes("Bus")) {
                delete detailed_quotation_data.buses;
            }
            if (!detailed_quotation_data.selected_categories.includes("Holiday")) {
                delete detailed_quotation_data.days;
            }

            let dates = "";
            if (formData.start_date && formData.end_date) {
                dates = `${formData.start_date} - ${formData.end_date}`;
            } else if (formData.start_date) {
                dates = `${formData.start_date}`;
            }

            const customerName = request?.client_name || "John Doe";
            const salesperson = {
                name: user?.name || "Agent Traveego",
                email: user?.email || "sales@traveego.com",
                phone: user?.phone || "+91 99999 99999",
            };

            const detailed_quotation_data_inr = convertQuotationDataToINR(detailed_quotation_data, inputCurrencyRate);

            let formatted_expiry_date = "";
            if (expiry_date) {
                try {
                    const [year, monthStr, dayStr] = expiry_date.split("-");
                    const monthIndex = parseInt(monthStr, 10) - 1;
                    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    const monthName = months[monthIndex] || "";
                    formatted_expiry_date = `${parseInt(dayStr, 10)} ${monthName} ${year}`;
                } catch (e) {
                    formatted_expiry_date = expiry_date;
                }
            }

            const quotation_data_formated = {
                ...detailed_quotation_data_inr,
                request_id: requestId || request?.id,
                expiry_date: formatted_expiry_date,
                dates,
                customerName,
                salesperson,
                orientation,
            };

            // Compile the template content client-side using Nunjucks
            const renderedHtml = getTemplateEnv().renderString(pdfTemplate, {
                data: quotation_data_formated,
            });

            // Prepend a premium warning banner indicating these are preview-only placeholder details
            const bannerHtml = `
<div style="background-color: #fffbeb; border-bottom: 2px solid #f59e0b; color: #b45309; text-align: center; padding: 12px; font-weight: 600; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size: 14px; position: sticky; top: 0; z-index: 9999; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
    ⚠️ Preview Mode: Salesperson contact and customer name are placeholders. Actual values are populated when saving.
</div>`;

            const finalHtml = bannerHtml + renderedHtml;

            const newWindow = window.open("", "_blank");
            if (newWindow) {
                newWindow.document.write(finalHtml);
                newWindow.document.close();
            } else {
                toast.error("Popup blocker prevented opening the preview window.");
            }
        } catch (error) {
            console.error("Error previewing quotation:", error);
            toast.error("Failed to generate preview");
        } finally {
            setPreviewing(false);
        }
    };

    useEffect(() => {
        if (
            formData.start_date &&
            formData.end_date &&
            selectedCategories.includes("Holiday")
        ) {
            setFormData((prev) => {
                const targetDayCount = Math.round(
                    (new Date(formData.end_date) - new Date(formData.start_date)) / (1000 * 60 * 60 * 24)
                ) + 1;

                if (prev.days && prev.days.length === targetDayCount) {
                    return prev;
                }

                const newDays = generateDays(targetDayCount);
                if (prev.days && prev.days.length > 0) {
                    return {
                        ...prev,
                        days: newDays.map((day, idx) => prev.days[idx] ? { ...prev.days[idx], date: day.date } : day)
                    };
                }

                return {
                    ...prev,
                    days: newDays,
                };
            });
        }
    }, [
        formData.start_date,
        formData.end_date,
        selectedCategories,
        selectedCategories.length
    ]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent mx-auto"></div>
                    <p className="mt-4 text-gray-600">
                        Loading quotation builder...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between flex-col">
                <div className="flex items-center gap-4 me-auto">
                    <Button
                        variant="outline"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            Quotation Builder
                        </h1>
                        <p className="text-gray-600">
                            Create detailed quotation with category-specific
                            services
                        </p>
                    </div>
                </div>
                <div className="flex gap-2 ms-auto mt-2">
                    <Button
                        onClick={() => handlePreview()}
                        disabled={previewing}
                        className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                    >
                        {previewing ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent animate-spin"></div>
                                Generating Preview...
                            </>
                        ) : (
                            <>
                                <FileText className="w-4 h-4" />
                                Preview PDF
                            </>
                        )}
                    </Button>

                    <Button
                        onClick={() => handleSave()}
                        className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                    >
                        <Upload className="w-4 h-4" />
                        Save Draft
                    </Button>

                    <div className="flex items-center gap-2">
                        <Label htmlFor="orientation" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                            Orientation:
                        </Label>
                        <select
                            id="orientation"
                            value={orientation}
                            onChange={(e) => setOrientation(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm h-10"
                        >
                            <option value="portrait">Portrait</option>
                            <option value="landscape">Landscape</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <Label htmlFor="expiry_date" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                            Expiry Date:
                        </Label>
                        <Input
                            id="expiry_date"
                            type="date"
                            name="expiry_date"
                            className="border border-gray-300 rounded-lg px-3 py-2"
                            value={expiry_date}
                            onChange={(e) => setExpiryDate(e.target.value)}
                        />
                    </div>
                    <Button
                        onClick={() => handleSave("SENT", expiry_date)}
                        disabled={saving || !expiry_date}
                        className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                Save Quotation
                            </>
                        )}
                    </Button>
                </div>
            </div>

            <div className="space-y-6">
                {/* Trip Header */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                        <CardTitle className="flex items-center gap-2">
                            <MapPin className="w-5 h-5" />
                            Trip Header
                        </CardTitle>
                        {request && (
                            <div className="flex items-center flex-wrap gap-2.5 p-1.5 bg-gradient-to-r from-orange-50/60 to-amber-50/40 border border-orange-100 rounded-xl shadow-[0_2px_10px_-4px_rgba(249,115,22,0.12)] text-xs text-gray-700">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-orange-100/60 rounded-lg shadow-sm">
                                    <svg className="w-3.5 h-3.5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                                    <span className="font-semibold text-gray-800">{request.client_name || "N/A"}</span>
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-orange-100/60 rounded-lg shadow-sm">
                                    <svg className="w-3.5 h-3.5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                                    <span className="text-gray-600 font-medium">{request.client_email || "N/A"}</span>
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-orange-100/60 rounded-lg shadow-sm">
                                    <svg className="w-3.5 h-3.5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                                    <span className="text-gray-600 font-medium">{request.client_country_code || ""} {request.client_phone || ""}</span>
                                </div>
                            </div>
                        )}
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap">
                            <div className="w-1/2 pe-1 pt-4">
                                <Label htmlFor="tripTitle">Trip Title</Label>
                                <Input
                                    id="tripTitle"
                                    value={formData.tripTitle}
                                    onChange={(e) =>
                                        handleInputChange(
                                            null,
                                            "tripTitle",
                                            e.target.value,
                                        )
                                    }
                                    placeholder="e.g., 5 Days Kashmir Adventure"
                                />
                            </div>
                            <div className="w-1/2 ps-1 pt-4">
                                <Label htmlFor="city">Destination</Label>
                                <Input
                                    id="city"
                                    value={formData.city}
                                    onChange={(e) =>
                                        handleInputChange(
                                            null,
                                            "city",
                                            e.target.value,
                                        )
                                    }
                                    placeholder="e.g., Srinagar"
                                />
                            </div>
                            <div className="w-1/2 pe-1 pt-4">
                                <Label htmlFor="start_date">Start Date</Label>
                                <Input
                                    id="start_date"
                                    value={formData.start_date}
                                    min={new Date().toISOString().split("T")[0]}
                                    onChange={(e) =>
                                        handleInputChange(
                                            null,
                                            "start_date",
                                            e.target.value,
                                        )
                                    }
                                    type="date"
                                />
                            </div>
                            <div className="w-1/2 ps-1 pt-4">
                                <Label htmlFor="end_date">End Date</Label>
                                <Input
                                    id="end_date"
                                    value={formData.end_date}
                                    min={formData.start_date}
                                    onChange={(e) =>
                                        handleInputChange(
                                            null,
                                            "end_date",
                                            e.target.value,
                                        )
                                    }
                                    type="date"
                                />
                            </div>

                            <div className="w-full pt-6 border-t border-dashed mt-6">
                                <Label className="text-sm font-bold text-gray-700 block mb-2">Traveler Breakdown</Label>
                                <div className="grid grid-cols-4 gap-4">
                                    <div>
                                        <Label htmlFor="adults">Adults *</Label>
                                        <Input
                                            id="adults"
                                            type="number"
                                            min="1"
                                            value={formData.summary.adults || 1}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "summary",
                                                    "adults",
                                                    parseInt(e.target.value) || 1,
                                                )
                                            }
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="children">Children</Label>
                                        <Input
                                            id="children"
                                            type="number"
                                            min="0"
                                            value={formData.summary.children || 0}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "summary",
                                                    "children",
                                                    parseInt(e.target.value) || 0,
                                                )
                                            }
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="infants">Infants</Label>
                                        <Input
                                            id="infants"
                                            type="number"
                                            min="0"
                                            value={formData.summary.infants || 0}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    "summary",
                                                    "infants",
                                                    parseInt(e.target.value) || 0,
                                                )
                                            }
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="total-travelers">Total Travelers</Label>
                                        <Input
                                            id="total-travelers"
                                            type="number"
                                            value={formData.summary.travelers}
                                            disabled
                                            className="bg-gray-50 border-gray-200 font-bold text-orange-600"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="w-1/2 pe-1 pt-4">
                                <Label htmlFor="bookingRef">
                                    Booking Reference
                                </Label>
                                <Input
                                    id="bookingRef"
                                    value={formData.bookingRef}
                                    disabled={true}
                                />
                            </div>
                            <div className="w-1/2 ps-1 pt-4">
                                <Label htmlFor="coverImage">
                                    Cover Image URL
                                </Label>
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        onClick={() => setCoverPickerOpen(true)}
                                        variant="outline"
                                        className="border-gray-200 hover:bg-gray-50 rounded-xl"
                                    >
                                        Choose Image
                                    </Button>
                                    <Input
                                        id="coverImage"
                                        value={formData.coverImage}
                                        onChange={(e) =>
                                            handleInputChange(
                                                null,
                                                "coverImage",
                                                e.target.value,
                                            )
                                        }
                                        placeholder="No image selected"
                                        className="flex-1"
                                    />
                                    <Button
                                        type="button"
                                        onClick={() => setCoverUploadOpen(true)}
                                        className="bg-orange-600 hover:bg-orange-700 text-white px-3"
                                        title="Upload New Image to S3"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                    {formData.coverImage && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => window.open(formData.coverImage, "_blank")}
                                            title="View Image"
                                            className="px-3"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div className="w-1/2 pe-1 pt-4">
                                <Label htmlFor="inputCurrency">Input Currency</Label>
                                <div className="flex gap-2">
                                    <Select
                                        value={isCustomCurrency ? "CUSTOM" : inputCurrency}
                                        onValueChange={(val) => {
                                            if (val === "CUSTOM") {
                                                setIsCustomCurrency(true);
                                                setInputCurrency("");
                                                setInputCurrencyRate(1);
                                            } else {
                                                setIsCustomCurrency(false);
                                                setInputCurrency(val);
                                                if (val === "INR") {
                                                    setInputCurrencyRate(1);
                                                } else {
                                                    const found = currencyRates.find(r => r.currency_code === val);
                                                    if (found) {
                                                        setInputCurrencyRate(Number(found.conversion_rate));
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        <SelectTrigger id="inputCurrency" className="w-full">
                                            <SelectValue placeholder="Select Currency" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="INR">INR (Indian Rupee)</SelectItem>
                                            {currencyRates.map((rate) => (
                                                <SelectItem key={rate.currency_code} value={rate.currency_code}>
                                                    {rate.currency_code}
                                                </SelectItem>
                                            ))}
                                            <SelectItem value="CUSTOM">Custom...</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {isCustomCurrency && (
                                        <Input
                                            placeholder="Code (e.g. USD)"
                                            value={inputCurrency}
                                            onChange={(e) => setInputCurrency(e.target.value.toUpperCase())}
                                            className="w-1/2"
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="w-1/2 ps-1 pt-4">
                                <Label htmlFor="inputCurrencyRate">
                                    Conversion Rate (1 {inputCurrency || "Currency"} = X INR)
                                </Label>
                                <Input
                                    id="inputCurrencyRate"
                                    type="number"
                                    value={inputCurrencyRate}
                                    disabled={inputCurrency === "INR"}
                                    onChange={(e) => setInputCurrencyRate(Number(e.target.value) || 1)}
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Category Selection */}
                <Card>
                    <CardHeader>
                        <CardTitle>Service Categories</CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                            Pre-selected based on customer request. You can
                            modify the selection.
                        </p>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-4 gap-4">
                            {availableCategories.map((category) => {
                                const Icon = category.icon;
                                const isSelected = selectedCategories.includes(
                                    category.value,
                                );
                                return (
                                    <div
                                        key={category.value}
                                        onClick={() =>
                                            toggleCategory(category.value)
                                        }
                                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${isSelected
                                            ? "border-orange-600 bg-orange-50"
                                            : "border-gray-200 hover:border-orange-300"
                                            }`}
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <Icon
                                                className={`w-8 h-8 ${isSelected ? "text-orange-600" : "text-gray-400"}`}
                                            />
                                            <span
                                                className={`font-medium text-center ${isSelected ? "text-orange-600" : "text-gray-700"}`}
                                            >
                                                {category.label}
                                            </span>
                                            {isSelected && (
                                                <Badge className="bg-orange-600">
                                                    Selected
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                {/* Summary */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Trip Summary
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div>
                                <Label>Highlights</Label>
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {formData.summary.highlights.map(
                                        (highlight, index) => (
                                            <Badge
                                                key={index}
                                                className="flex items-center gap-2"
                                            >
                                                {highlight}
                                                <button
                                                    onClick={() =>
                                                        removeHighlight(index)
                                                    }
                                                    className="hover:text-red-500"
                                                >
                                                    ×
                                                </button>
                                            </Badge>
                                        ),
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Input
                                        value={newHighlight}
                                        onChange={(e) =>
                                            setNewHighlight(e.target.value)
                                        }
                                        onKeyPress={(e) =>
                                            e.key === "Enter" && addHighlight()
                                        }
                                        placeholder="Add a highlight"
                                    />
                                    <Button
                                        onClick={addHighlight}
                                        variant="outline"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* CATEGORY-SPECIFIC SECTIONS */}

                {/* Flight Section */}
                {selectedCategories.includes("Flight") && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Plane className="w-5 h-5" />
                                    Flight Details
                                </div>
                                <Button
                                    onClick={() => addFlight()}
                                    variant="outline"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Flight
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {formData.flights.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No flights added. Click "Add Flight" to
                                    begin.
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {formData.flights.map(
                                        (flight, flightIdx) => (
                                            <div
                                                key={flight.id}
                                                className="border rounded-lg p-4"
                                            >
                                                <div className="flex items-center justify-between mb-4">
                                                    <h3 className="text-lg font-semibold">
                                                        Flight {flightIdx + 1}
                                                    </h3>
                                                    <Button
                                                        onClick={() =>
                                                            removeFlight(
                                                                flightIdx,
                                                            )
                                                        }
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-red-500"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4 mb-4">
                                                    <div>
                                                        <Label>
                                                            Journey Type
                                                        </Label>
                                                        <select
                                                            value={
                                                                flight.journey_type
                                                            }
                                                            onChange={(e) => {
                                                                const updated =
                                                                    [
                                                                        ...formData.flights,
                                                                    ];
                                                                updated[
                                                                    flightIdx
                                                                ].journey_type =
                                                                    e.target.value;
                                                                setFormData(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        flights:
                                                                            updated,
                                                                    }),
                                                                );
                                                            }}
                                                            className="w-full px-3 py-2 border rounded-lg"
                                                        >
                                                            <option>
                                                                One-way
                                                            </option>
                                                            <option>
                                                                Round-trip
                                                            </option>
                                                            <option>
                                                                Multi-city
                                                            </option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Total Passengers
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                flight.total_passengers
                                                            }
                                                            onChange={(e) => {
                                                                // const updated =
                                                                //     [
                                                                //         ...formData.flights,
                                                                //     ];
                                                                // updated[
                                                                //     flightIdx
                                                                // ].total_passengers =
                                                                //     parseInt(
                                                                //         e.target
                                                                //             .value,
                                                                //     );
                                                                // setFormData(
                                                                //     (prev) => ({
                                                                //         ...prev,
                                                                //         flights:
                                                                //             updated,
                                                                //     }),
                                                                // );
                                                                updateFlightField(
                                                                    flightIdx,
                                                                    "total_passengers",
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                );
                                                            }}
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Cost Per Person
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                flight.cost_per_person
                                                            }
                                                            onChange={(e) => {
                                                                updateFlightField(
                                                                    flightIdx,
                                                                    "cost_per_person",
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                );
                                                            }}
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Total Cost
                                                            (Auto-calculated)
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                flight.total_cost
                                                            }
                                                            disabled
                                                        />
                                                    </div>
                                                </div>

                                                <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
                                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-slate-200">
                                                        <div>
                                                            <Label className="text-base font-semibold text-slate-800">
                                                                Flight Itinerary Details
                                                            </Label>
                                                            <p className="text-xs text-slate-500 mt-0.5">
                                                                Upload a flight screenshot or enter segment details manually.
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-1 bg-white border border-slate-300 rounded-lg p-1 shadow-xs">
                                                            <button
                                                                type="button"
                                                                onClick={() => updateFlightField(flightIdx, "input_mode", "screenshot")}
                                                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${(flight.input_mode || (!flight.segments?.length ? "screenshot" : "manual")) === "screenshot"
                                                                        ? "bg-blue-600 text-white shadow-xs"
                                                                        : "text-slate-600 hover:bg-slate-100"
                                                                    }`}
                                                            >
                                                                <ImageIcon className="w-3.5 h-3.5" />
                                                                Screenshot
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => updateFlightField(flightIdx, "input_mode", "manual")}
                                                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${(flight.input_mode || (!flight.segments?.length ? "screenshot" : "manual")) === "manual"
                                                                        ? "bg-blue-600 text-white shadow-xs"
                                                                        : "text-slate-600 hover:bg-slate-100"
                                                                    }`}
                                                            >
                                                                <Plane className="w-3.5 h-3.5" />
                                                                Manual Segments {flight.segments?.length ? `(${flight.segments.length})` : ""}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* SCREENSHOT MODE */}
                                                    {(flight.input_mode || (!flight.segments?.length ? "screenshot" : "manual")) === "screenshot" && (
                                                        <div className="space-y-4">
                                                            {flight.screenshot ? (
                                                                <div className="relative border-2 border-dashed border-blue-200 bg-white rounded-lg p-4 text-center">
                                                                    <img
                                                                        src={flight.screenshot}
                                                                        alt="Flight details screenshot"
                                                                        className="max-h-72 mx-auto rounded object-contain border shadow-xs"
                                                                    />
                                                                    <div className="mt-3 flex items-center justify-center gap-3">
                                                                        <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors">
                                                                            <Upload className="w-3.5 h-3.5" />
                                                                            {uploadingFlightIdx === flightIdx ? "Uploading..." : "Replace Image"}
                                                                            <input
                                                                                type="file"
                                                                                accept="image/*"
                                                                                className="hidden"
                                                                                disabled={uploadingFlightIdx === flightIdx}
                                                                                onChange={(e) => {
                                                                                    if (e.target.files?.[0]) {
                                                                                        handleFlightScreenshotUpload(flightIdx, e.target.files[0]);
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </label>
                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                                                            onClick={() => updateFlightField(flightIdx, "screenshot", "")}
                                                                        >
                                                                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                                                                            Remove Screenshot
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="space-y-3">
                                                                    <div className="border-2 border-dashed border-slate-300 hover:border-blue-500 bg-white rounded-lg p-6 text-center transition-colors">
                                                                        <ImageIcon className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                                                                        <p className="text-sm font-medium text-slate-700 mb-1">
                                                                            Upload Flight Segments Screenshot
                                                                        </p>
                                                                        <p className="text-xs text-slate-500 mb-4">
                                                                            Attach an image of the flight itinerary / booking ticket screenshot
                                                                        </p>
                                                                        <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md shadow-xs transition-colors">
                                                                            <Upload className="w-4 h-4" />
                                                                            {uploadingFlightIdx === flightIdx ? "Uploading..." : "Choose Image File"}
                                                                            <input
                                                                                type="file"
                                                                                accept="image/*"
                                                                                className="hidden"
                                                                                disabled={uploadingFlightIdx === flightIdx}
                                                                                onChange={(e) => {
                                                                                    if (e.target.files?.[0]) {
                                                                                        handleFlightScreenshotUpload(flightIdx, e.target.files[0]);
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </label>
                                                                    </div>

                                                                    <div className="flex items-center gap-2">
                                                                        <div className="flex-1 h-px bg-slate-200"></div>
                                                                        <span className="text-xs text-slate-400 font-medium uppercase">Or Enter Direct Image URL</span>
                                                                        <div className="flex-1 h-px bg-slate-200"></div>
                                                                    </div>

                                                                    <div>
                                                                        <Input
                                                                            placeholder="Paste direct image URL (https://...)"
                                                                            value={flight.screenshot || ""}
                                                                            onChange={(e) => updateFlightField(flightIdx, "screenshot", e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* MANUAL SEGMENTS MODE */}
                                                    {(flight.input_mode || (!flight.segments?.length ? "screenshot" : "manual")) === "manual" && (
                                                        <div>
                                                            <div className="flex items-center justify-between mb-3">
                                                                <Label className="text-sm font-semibold text-slate-700">
                                                                    Manual Flight Segments ({flight.segments?.length || 0})
                                                                </Label>
                                                                <Button
                                                                    type="button"
                                                                    onClick={() => addFlightSegment(flightIdx)}
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                                                >
                                                                    <Plus className="w-3.5 h-3.5 mr-1" />
                                                                    Add Segment
                                                                </Button>
                                                            </div>

                                                            {(!flight.segments || flight.segments.length === 0) ? (
                                                                <div className="text-center py-6 border border-dashed rounded-lg bg-white text-slate-500 text-xs">
                                                                    No manual segments added yet. Click "Add Segment" above.
                                                                </div>
                                                            ) : (
                                                                flight.segments.map(
                                                                    (segment, segIdx) => (
                                                                        <div
                                                                            key={segment.id}
                                                                            className="bg-gray-50 p-4 rounded-lg mb-3 space-y-4"
                                                                        >
                                                                            {/* Header */}
                                                                            <div className="flex items-center justify-between">
                                                                                <span className="font-medium text-sm">
                                                                                    Segment{" "}
                                                                                    {segIdx +
                                                                                        1}
                                                                                </span>
                                                                                <Button
                                                                                    onClick={() =>
                                                                                        removeFlightSegment(
                                                                                            flightIdx,
                                                                                            segIdx,
                                                                                        )
                                                                                    }
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    className="text-red-500"
                                                                                >
                                                                                    <Trash2 className="w-4 h-4" />
                                                                                </Button>
                                                                            </div>

                                                                            {/* Flight info */}
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                                                                <div>
                                                                                    <Label>Flight Number</Label>
                                                                                    <Input
                                                                                        placeholder="Flight Number"
                                                                                        value={
                                                                                            segment.flight_number
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "flight_number",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>Airline</Label>
                                                                                    <AirlineAutocomplete
                                                                                        placeholder="Airline"
                                                                                        value={segment.airline}
                                                                                        options={flights}
                                                                                        onChange={(val, item) => {
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "airline",
                                                                                                val
                                                                                            );
                                                                                            if (item && item.base_data_json?.airline_logo) {
                                                                                                updateFlightSegment(
                                                                                                    flightIdx,
                                                                                                    segIdx,
                                                                                                    "airline_logo",
                                                                                                    item.base_data_json.airline_logo
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>Airline Logo URL</Label>
                                                                                    <Input
                                                                                        placeholder="Airline Logo URL"
                                                                                        value={
                                                                                            segment.airline_logo
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "airline_logo",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>Cabin Class</Label>
                                                                                    <Select
                                                                                        value={segment.cabin_class}
                                                                                        onValueChange={(val) =>
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "cabin_class",
                                                                                                val,
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <SelectTrigger className="w-full">
                                                                                            <SelectValue placeholder="Cabin Class" />
                                                                                        </SelectTrigger>
                                                                                        <SelectContent>
                                                                                            <SelectItem value="Economy">Economy</SelectItem>
                                                                                            <SelectItem value="Premium Economy">Premium Economy</SelectItem>
                                                                                            <SelectItem value="Business">Business</SelectItem>
                                                                                            <SelectItem value="First Class">First Class</SelectItem>
                                                                                        </SelectContent>
                                                                                    </Select>
                                                                                </div>
                                                                            </div>

                                                                            {/* Departure */}
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                                                                <div>
                                                                                    <Label>Departure Airport</Label>
                                                                                    <AirportAutocomplete
                                                                                        placeholder="Departure Airport"
                                                                                        value={
                                                                                            segment.departure_airport
                                                                                        }
                                                                                        onChange={(
                                                                                            val,
                                                                                            airportObj
                                                                                        ) => {
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "departure_airport",
                                                                                                val,
                                                                                            );
                                                                                            if (airportObj && airportObj.municipality) {
                                                                                                updateFlightSegment(
                                                                                                    flightIdx,
                                                                                                    segIdx,
                                                                                                    "departure_city",
                                                                                                    airportObj.municipality,
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>Departure City</Label>
                                                                                    <Input
                                                                                        placeholder="Departure City"
                                                                                        value={
                                                                                            segment.departure_city
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "departure_city",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>
                                                                                        Departure
                                                                                        Date
                                                                                    </Label>
                                                                                    <Input
                                                                                        type="date"
                                                                                        value={
                                                                                            segment.departure_date
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "departure_date",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                        min={
                                                                                            new Date()
                                                                                                .toISOString()
                                                                                                .split(
                                                                                                    "T",
                                                                                                )[0]
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>
                                                                                        Departure
                                                                                        Time
                                                                                    </Label>
                                                                                    <Input
                                                                                        type="time"
                                                                                        value={
                                                                                            segment.departure_time
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "departure_time",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </div>

                                                                            {/* Arrival */}
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                                                                <div>
                                                                                    <Label>Arrival Airport</Label>
                                                                                    <AirportAutocomplete
                                                                                        placeholder="Arrival Airport"
                                                                                        value={
                                                                                            segment.arrival_airport
                                                                                        }
                                                                                        onChange={(
                                                                                            val,
                                                                                            airportObj
                                                                                        ) => {
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "arrival_airport",
                                                                                                val,
                                                                                            );
                                                                                            if (airportObj && airportObj.municipality) {
                                                                                                updateFlightSegment(
                                                                                                    flightIdx,
                                                                                                    segIdx,
                                                                                                    "arrival_city",
                                                                                                    airportObj.municipality,
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>Arrival City</Label>
                                                                                    <Input
                                                                                        placeholder="Arrival City"
                                                                                        value={
                                                                                            segment.arrival_city
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "arrival_city",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>
                                                                                        Arrival
                                                                                        Date
                                                                                    </Label>
                                                                                    <Input
                                                                                        type="date"
                                                                                        value={
                                                                                            segment.arrival_date
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "arrival_date",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            !segment.departure_date
                                                                                        }
                                                                                        min={
                                                                                            segment.departure_date
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>
                                                                                        Arrival
                                                                                        Time
                                                                                    </Label>
                                                                                    <Input
                                                                                        type="time"
                                                                                        value={
                                                                                            segment.arrival_time
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updateFlightSegment(
                                                                                                flightIdx,
                                                                                                segIdx,
                                                                                                "arrival_time",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </div>

                                                                            {/* Extra details */}
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                                                                <div>
                                                                                    <Label>Duration</Label>
                                                                                    <Input
                                                                                        placeholder="e.g. 2H 30M"
                                                                                        value={segment.duration}
                                                                                        onChange={(e) => updateFlightSegment(flightIdx, segIdx, "duration", e.target.value)}
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>Baggage Check-in</Label>
                                                                                    <Input
                                                                                        placeholder="e.g. 15 KG or 2 X 23 KG"
                                                                                        value={segment.baggage_checkin}
                                                                                        onChange={(e) => updateFlightSegment(flightIdx, segIdx, "baggage_checkin", e.target.value)}
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label>Baggage Cabin</Label>
                                                                                    <Input
                                                                                        placeholder="e.g. 7 KG"
                                                                                        value={segment.baggage_cabin}
                                                                                        onChange={(e) => updateFlightSegment(flightIdx, segIdx, "baggage_cabin", e.target.value)}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <Label>GST %</Label>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            value={
                                                                flight.gst_percentage
                                                            }
                                                            onChange={(e) => {
                                                                updateFlightField(
                                                                    flightIdx,
                                                                    "gst_percentage",
                                                                    e.target
                                                                        .value,
                                                                );
                                                            }}
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Notes</Label>
                                                        <Textarea
                                                            value={flight.notes}
                                                            onChange={(e) => {
                                                                const updated =
                                                                    [
                                                                        ...formData.flights,
                                                                    ];
                                                                updated[
                                                                    flightIdx
                                                                ].notes =
                                                                    e.target.value;
                                                                setFormData(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        flights:
                                                                            updated,
                                                                    }),
                                                                );
                                                            }}
                                                            rows="2"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Visa Section */}
                {selectedCategories.includes("Visa") && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-5 h-5" />
                                    Visa Details
                                </div>
                                <Button
                                    onClick={() => {
                                        if (visas.length > 0) {
                                            setSearchTerm("");
                                            setShowVisaModal(true);
                                        } else {
                                            addVisa();
                                        }
                                    }}
                                    variant="outline"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Visa
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {formData.visas.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No visas added. Click "Add Visa" to begin.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {formData.visas.map((visa, visaIdx) => (
                                        <div
                                            key={visa.id}
                                            className="border rounded-lg p-4"
                                        >
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-lg font-semibold">
                                                    Visa {visaIdx + 1}
                                                </h3>
                                                <Button
                                                    onClick={() =>
                                                        removeVisa(visaIdx)
                                                    }
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-red-500"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>

                                            <div className="grid grid-cols-3 gap-4">
                                                <div>
                                                    <Label>Visa Name</Label>
                                                    <Input
                                                        value={visa.name}
                                                        onChange={(e) =>
                                                            updateVisa(
                                                                visaIdx,
                                                                "name",
                                                                e.target.value,
                                                            )
                                                        }
                                                        placeholder="e.g., UAE Business Visa"
                                                    />
                                                </div>
                                                <div>
                                                    <Label>Visa Type</Label>
                                                    <select
                                                        value={visa.visa_type}
                                                        onChange={(e) =>
                                                            updateVisa(
                                                                visaIdx,
                                                                "visa_type",
                                                                e.target.value,
                                                            )
                                                        }
                                                        className="w-full px-3 py-2 border rounded-lg"
                                                    >
                                                        <option>Tourist</option>
                                                        <option>
                                                            Business
                                                        </option>
                                                        <option>Transit</option>
                                                        <option>Student</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <Label>
                                                        Destination Country
                                                    </Label>
                                                    <Input
                                                        value={
                                                            visa.destination_country
                                                        }
                                                        onChange={(e) =>
                                                            updateVisa(
                                                                visaIdx,
                                                                "destination_country",
                                                                e.target.value,
                                                            )
                                                        }
                                                        placeholder="e.g., United Arab Emirates"
                                                    />
                                                </div>
                                                <div>
                                                    <Label>
                                                        Processing Time (Days)
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        value={
                                                            visa.processing_time_days
                                                        }
                                                        onChange={(e) =>
                                                            updateVisa(
                                                                visaIdx,
                                                                "processing_time_days",
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                        min="0"
                                                    />
                                                </div>
                                                <div>
                                                    <Label>
                                                        Cost Per Person
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        value={
                                                            visa.cost_per_person
                                                        }
                                                        onChange={(e) =>
                                                            updateVisa(
                                                                visaIdx,
                                                                "cost_per_person",
                                                                parseFloat(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                        min="0"
                                                    />
                                                </div>
                                                <div>
                                                    <Label>
                                                        Number of People
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        value={
                                                            visa.number_of_people
                                                        }
                                                        onChange={(e) =>
                                                            updateVisa(
                                                                visaIdx,
                                                                "number_of_people",
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                        min="0"
                                                    />
                                                </div>
                                                <div>
                                                    <Label>GST %</Label>
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={
                                                            visa.gst_percentage
                                                        }
                                                        onChange={(e) =>
                                                            updateVisa(
                                                                visaIdx,
                                                                "gst_percentage",
                                                                parseFloat(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                        min="0"
                                                    />
                                                </div>
                                                <div>
                                                    <Label>
                                                        Total Cost
                                                        (Auto-calculated)
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        value={visa.total_cost}
                                                        disabled
                                                    />
                                                </div>
                                                <div className="col-span-3">
                                                    <Label>Description</Label>
                                                    <Textarea
                                                        value={visa.description}
                                                        onChange={(e) =>
                                                            updateVisa(
                                                                visaIdx,
                                                                "description",
                                                                e.target.value,
                                                            )
                                                        }
                                                        rows="2"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Transport Section */}
                {selectedCategories.includes("Transport") && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Car className="w-5 h-5" />
                                    Transport Within City
                                </div>
                                <Button
                                    onClick={() => {
                                        if (transports.length > 0) {
                                            setSearchTerm("");
                                            setShowTransportModal(true);
                                        } else {
                                            addTransport();
                                        }
                                    }}
                                    variant="outline"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Transport
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {formData.transports_within_city.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No transport added. Click "Add Transport" to
                                    begin.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {formData.transports_within_city.map(
                                        (transport, transportIdx) => (
                                            <div
                                                key={transport.id}
                                                className="border rounded-lg p-4"
                                            >
                                                <div className="flex items-center justify-between mb-4">
                                                    <h3 className="text-lg font-semibold">
                                                        Transport{" "}
                                                        {transportIdx + 1}
                                                    </h3>
                                                    <Button
                                                        onClick={() =>
                                                            removeTransport(
                                                                transportIdx,
                                                            )
                                                        }
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-red-500"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>

                                                <div className="grid grid-cols-3 gap-4">
                                                    <div>
                                                        <Label>
                                                            Vehicle Type
                                                        </Label>
                                                        <select
                                                            value={
                                                                transport.vehicle_type
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "vehicle_type",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="w-full px-3 py-2 border rounded-lg"
                                                        >
                                                            <option>
                                                                Sedan
                                                            </option>
                                                            <option>SUV</option>
                                                            <option>
                                                                Mini Bus
                                                            </option>
                                                            <option>Bus</option>
                                                            <option>
                                                                Tempo Traveller
                                                            </option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Vehicle Name
                                                        </Label>
                                                        <Input
                                                            value={
                                                                transport.vehicle_name
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "vehicle_name",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="e.g., Toyota Hiace"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Capacity</Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                transport.capacity
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "capacity",
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Pickup Location
                                                        </Label>
                                                        <Input
                                                            value={
                                                                transport.pickup_location
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "pickup_location",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Drop Location
                                                        </Label>
                                                        <Input
                                                            value={
                                                                transport.drop_location
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "drop_location",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Pickup Date
                                                        </Label>
                                                        <Input
                                                            type="date"
                                                            value={
                                                                transport.pickup_date
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "pickup_date",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            min={
                                                                new Date()
                                                                    .toISOString()
                                                                    .split(
                                                                        "T",
                                                                    )[0]
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Pickup Time
                                                        </Label>
                                                        <Input
                                                            type="time"
                                                            value={
                                                                transport.pickup_time
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "pickup_time",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Duration</Label>
                                                        <Input
                                                            value={
                                                                transport.duration
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "duration",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="e.g., 2 hours"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Cost Per Vehicle
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                transport.cost_per_vehicle
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "cost_per_vehicle",
                                                                    parseFloat(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Number of Vehicles
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                transport.number_of_vehicles
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "number_of_vehicles",
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>GST %</Label>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            value={
                                                                transport.gst_percentage
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "gst_percentage",
                                                                    parseFloat(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Total Cost
                                                            (Auto-calculated)
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                transport.total_cost
                                                            }
                                                            disabled
                                                        />
                                                    </div>
                                                    <div className="col-span-2">
                                                        <Label>
                                                            Driver Details
                                                        </Label>
                                                        <Input
                                                            value={
                                                                transport.driver_details
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "driver_details",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="col-span-3">
                                                        <Label>Notes</Label>
                                                        <Textarea
                                                            value={
                                                                transport.notes
                                                            }
                                                            onChange={(e) =>
                                                                updateTransport(
                                                                    transportIdx,
                                                                    "notes",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            rows="2"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* MICE Events Section */}
                {selectedCategories.includes("MICE") && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Building2 className="w-5 h-5" />
                                    MICE Events
                                </div>
                                <Button
                                    onClick={() => {
                                        if (miceEvents.length > 0) {
                                            setSearchTerm("");
                                            setShowAddMiceEventModal(true);
                                        } else {
                                            addMiceEvent();
                                        }
                                    }}
                                    variant="outline"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Event
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {formData.mice_events.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No MICE events added. Click "Add Event" to
                                    begin.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {formData.mice_events.map(
                                        (event, eventIdx) => (
                                            <div
                                                key={event.id}
                                                className="border rounded-lg p-4"
                                            >
                                                <div className="flex items-center justify-between mb-4">
                                                    <h3 className="text-lg font-semibold">
                                                        Event {eventIdx + 1}
                                                    </h3>
                                                    <Button
                                                        onClick={() =>
                                                            removeMiceEvent(
                                                                eventIdx,
                                                            )
                                                        }
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-red-500"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>

                                                <div className="grid grid-cols-3 gap-4">
                                                    <div>
                                                        <Label>
                                                            Event Type
                                                        </Label>
                                                        <select
                                                            value={
                                                                event.event_type
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "event_type",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="w-full px-3 py-2 border rounded-lg"
                                                        >
                                                            <option>
                                                                Meeting
                                                            </option>
                                                            <option>
                                                                Conference
                                                            </option>
                                                            <option>
                                                                Exhibition
                                                            </option>
                                                            <option>
                                                                Incentive
                                                            </option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Event Name
                                                        </Label>
                                                        <Input
                                                            value={
                                                                event.event_name
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "event_name",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Venue Name
                                                        </Label>
                                                        <Input
                                                            value={
                                                                event.venue_name
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "venue_name",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>

                                                    <div className="col-span-3">
                                                        <Label>
                                                            Venue Address
                                                        </Label>
                                                        <Input
                                                            value={
                                                                event.venue_address
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "venue_address",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Capacity</Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                event.capacity
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "capacity",
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Number of Attendees
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                event.number_of_attendees
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "number_of_attendees",
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    </div>

                                                    <div>
                                                        <Label>Catering</Label>
                                                        <select
                                                            value={
                                                                event.catering_included
                                                                    ? "true"
                                                                    : "false"
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "catering_included",
                                                                    e.target
                                                                        .value ===
                                                                    "true",
                                                                )
                                                            }
                                                            className="w-full px-3 py-2 border rounded-lg"
                                                        >
                                                            <option value="false">
                                                                Catering not
                                                                included
                                                            </option>
                                                            <option value="true">
                                                                Catering
                                                                included
                                                            </option>
                                                        </select>
                                                    </div>

                                                    {event.catering_included && (
                                                        <div className="mt-2">
                                                            <Label>
                                                                Catering Details
                                                            </Label>
                                                            <Input
                                                                type="text"
                                                                value={
                                                                    event.catering_details
                                                                }
                                                                onChange={(e) =>
                                                                    updateMiceEvent(
                                                                        eventIdx,
                                                                        "catering_details",
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                placeholder="Enter catering details"
                                                            />
                                                        </div>
                                                    )}

                                                    <div>
                                                        <Label>
                                                            Event Date
                                                        </Label>
                                                        <Input
                                                            type="date"
                                                            value={
                                                                event.event_date
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "event_date",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            min={
                                                                new Date()
                                                                    .toISOString()
                                                                    .split(
                                                                        "T",
                                                                    )[0]
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Event Time
                                                        </Label>
                                                        <Input
                                                            value={
                                                                event.event_time
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "event_time",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="e.g., 09:00 AM - 05:00 PM"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Duration</Label>
                                                        <Input
                                                            value={
                                                                event.duration
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "duration",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="e.g., 8 hours"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Cost Per Person
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                event.cost_per_person
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "cost_per_person",
                                                                    parseFloat(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>GST %</Label>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            value={
                                                                event.gst_percentage
                                                            }
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "gst_percentage",
                                                                    parseFloat(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Total Cost
                                                            (Auto-calculated)
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                event.total_cost
                                                            }
                                                            disabled
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Notes</Label>
                                                        <Input
                                                            type="text"
                                                            value={event.notes}
                                                            onChange={(e) =>
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "notes",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mt-2">
                                                    <Label>
                                                        Equipment Provided
                                                    </Label>
                                                    <select
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value;
                                                            if (!value) return;

                                                            const allEquipments =
                                                                EQUIPMENT_OPTIONS;

                                                            // If "all" selected, fill all
                                                            if (
                                                                value === "all"
                                                            ) {
                                                                updateMiceEvent(
                                                                    eventIdx,
                                                                    "equipment_provided",
                                                                    allEquipments,
                                                                );
                                                            } else {
                                                                const current =
                                                                    event.equipment_provided ||
                                                                    [];
                                                                if (
                                                                    !current.includes(
                                                                        value,
                                                                    )
                                                                ) {
                                                                    updateMiceEvent(
                                                                        eventIdx,
                                                                        "equipment_provided",
                                                                        [
                                                                            ...current,
                                                                            value,
                                                                        ],
                                                                    );
                                                                }
                                                            }

                                                            e.target.value = ""; // reset dropdown
                                                        }}
                                                        className="w-full px-3 py-2 border rounded-lg"
                                                    >
                                                        <option value="">
                                                            Select equipment
                                                        </option>
                                                        {EQUIPMENT_OPTIONS.map(
                                                            (item) => (
                                                                <option
                                                                    key={item}
                                                                    value={item}
                                                                >
                                                                    {item
                                                                        .replace(
                                                                            /_/g,
                                                                            " ",
                                                                        )
                                                                        .replace(
                                                                            /\b\w/g,
                                                                            (
                                                                                c,
                                                                            ) =>
                                                                                c.toUpperCase(),
                                                                        )}
                                                                </option>
                                                            ),
                                                        )}
                                                        <option value="all">
                                                            All Equipment
                                                        </option>
                                                    </select>

                                                    {/* Selected chips */}
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {event.equipment_provided?.map(
                                                            (equipment) => (
                                                                <div
                                                                    key={
                                                                        equipment
                                                                    }
                                                                    className="flex items-center gap-1 px-2 py-1 bg-gray-200 rounded-full text-sm"
                                                                >
                                                                    {equipment.replace(
                                                                        /_/g,
                                                                        " ",
                                                                    )}
                                                                    <button
                                                                        onClick={() => {
                                                                            const updated =
                                                                                event.equipment_provided.filter(
                                                                                    (
                                                                                        e,
                                                                                    ) =>
                                                                                        e !==
                                                                                        equipment,
                                                                                );
                                                                            updateMiceEvent(
                                                                                eventIdx,
                                                                                "equipment_provided",
                                                                                updated,
                                                                            );
                                                                        }}
                                                                        className="text-red-500 ml-1"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Standalone Hotels Section */}
                {selectedCategories.includes("Hotel") && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Hotel className="w-5 h-5" />
                                    Standalone Hotels
                                </div>
                                <Button
                                    onClick={() => {
                                        if (hotels.length > 0) {
                                            setSearchTerm("");
                                            setShowAddHotelModal(true);
                                        } else {
                                            addStandaloneHotel();
                                        }
                                    }}
                                    variant="outline"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Hotel
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {formData.standalone_hotels.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No hotels added. Click "Add Hotel" to begin.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {formData.standalone_hotels.map(
                                        (hotel, hotelIdx) => (
                                            <div
                                                key={hotel.id}
                                                className="border rounded-lg p-4"
                                            >
                                                <div className="flex items-center justify-between mb-4">
                                                    <h3 className="text-lg font-semibold">
                                                        Hotel {hotelIdx + 1}
                                                    </h3>
                                                    <Button
                                                        onClick={() =>
                                                            removeStandaloneHotel(
                                                                hotelIdx,
                                                            )
                                                        }
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-red-500"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>

                                                <div className="grid grid-cols-3 gap-4">
                                                    <div>
                                                        <Label>
                                                            Hotel Name
                                                        </Label>
                                                        <Input
                                                            value={hotel.name}
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "name",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Stars</Label>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            max="5"
                                                            value={hotel.stars}
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "stars",
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>City</Label>
                                                        <Input
                                                            value={hotel.city}
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "city",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Hotel Address
                                                        </Label>
                                                        <Input
                                                            value={
                                                                hotel.address
                                                            }
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "address",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="Address"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Check-in Date
                                                        </Label>
                                                        <Input
                                                            type="date"
                                                            value={
                                                                hotel.check_in_date
                                                            }
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "check_in_date",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            min={
                                                                new Date()
                                                                    .toISOString()
                                                                    .split(
                                                                        "T",
                                                                    )[0]
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Check-out Date
                                                        </Label>
                                                        <Input
                                                            type="date"
                                                            value={
                                                                hotel.check_out_date
                                                            }
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "check_out_date",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            min={
                                                                hotel.check_in_date
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Number of Nights
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                hotel.number_of_nights
                                                            }
                                                            disabled
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Room Type</Label>
                                                        <Input
                                                            value={
                                                                hotel.room_type
                                                            }
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "room_type",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Number of Rooms
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                hotel.number_of_rooms
                                                            }
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "number_of_rooms",
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Guests Per Room
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                hotel.guests_per_room
                                                            }
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "guests_per_room",
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Meal Plan</Label>
                                                        <select
                                                            value={
                                                                hotel.meal_plan
                                                            }
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "meal_plan",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="w-full px-3 py-2 border rounded-lg"
                                                        >
                                                            <option>
                                                                EP (Room Only)
                                                            </option>
                                                            <option>
                                                                CP (Breakfast)
                                                            </option>
                                                            <option>
                                                                MAP (Breakfast &
                                                                Dinner)
                                                            </option>
                                                            <option>
                                                                AP (All Meals)
                                                            </option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Cost Per Room Per
                                                            Night
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                hotel.cost_per_room_per_night
                                                            }
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "cost_per_room_per_night",
                                                                    parseFloat(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Amenities</Label>

                                                        {/* Dropdown */}
                                                        <select
                                                            onChange={(e) => {
                                                                const value =
                                                                    e.target
                                                                        .value;
                                                                if (!value)
                                                                    return;

                                                                const allAmenities =
                                                                    AMENITY_OPTIONS;

                                                                // If "all" selected, fill all
                                                                if (
                                                                    value ===
                                                                    "all"
                                                                ) {
                                                                    updateStandaloneHotel(
                                                                        hotelIdx,
                                                                        "amenities",
                                                                        allAmenities,
                                                                    );
                                                                } else {
                                                                    const current =
                                                                        hotel.amenities ||
                                                                        [];
                                                                    if (
                                                                        !current.includes(
                                                                            value,
                                                                        )
                                                                    ) {
                                                                        updateStandaloneHotel(
                                                                            hotelIdx,
                                                                            "amenities",
                                                                            [
                                                                                ...current,
                                                                                value,
                                                                            ],
                                                                        );
                                                                    }
                                                                }

                                                                e.target.value =
                                                                    ""; // reset dropdown
                                                            }}
                                                            className="w-full px-3 py-2 border rounded-lg"
                                                        >
                                                            <option value="">
                                                                Select amenity
                                                            </option>
                                                            {AMENITY_OPTIONS.map(
                                                                (amenity) => (
                                                                    <option
                                                                        key={
                                                                            amenity
                                                                        }
                                                                        value={
                                                                            amenity
                                                                        }
                                                                    >
                                                                        {amenity
                                                                            .charAt(
                                                                                0,
                                                                            )
                                                                            .toUpperCase() +
                                                                            amenity.slice(
                                                                                1,
                                                                            )}
                                                                    </option>
                                                                ),
                                                            )}
                                                            <option value="all">
                                                                All Amenities
                                                            </option>
                                                        </select>

                                                        {/* Selected chips */}
                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                            {hotel.amenities?.map(
                                                                (amenity) => (
                                                                    <div
                                                                        key={
                                                                            amenity
                                                                        }
                                                                        className="flex items-center gap-1 px-2 py-1 bg-gray-200 rounded-full text-sm"
                                                                    >
                                                                        {
                                                                            amenity
                                                                        }
                                                                        <button
                                                                            onClick={() => {
                                                                                const updated =
                                                                                    hotel.amenities.filter(
                                                                                        (
                                                                                            a,
                                                                                        ) =>
                                                                                            a !==
                                                                                            amenity,
                                                                                    );
                                                                                updateStandaloneHotel(
                                                                                    hotelIdx,
                                                                                    "amenities",
                                                                                    updated,
                                                                                );
                                                                            }}
                                                                            className="text-red-500 ml-1"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </div>
                                                                ),
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Hotel Website / URL
                                                        </Label>
                                                        <Input
                                                            value={hotel.url}
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "url",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="Website / URL"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Hotel Image
                                                        </Label>
                                                        <Input
                                                            value={hotel.image}
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "image",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="Hotel Image URL"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>GST %</Label>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            value={
                                                                hotel.gst_percentage
                                                            }
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "gst_percentage",
                                                                    parseFloat(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>
                                                            Total Cost
                                                            (Auto-calculated)
                                                        </Label>
                                                        <Input
                                                            type="number"
                                                            value={
                                                                hotel.total_cost
                                                            }
                                                            disabled
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Notes</Label>
                                                        <Input
                                                            value={hotel.notes}
                                                            onChange={(e) =>
                                                                updateStandaloneHotel(
                                                                    hotelIdx,
                                                                    "notes",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="Notes"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Sightseeing Packages Section */}
                {selectedCategories.includes("Sightseeing") && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <ActivityIcon className="w-5 h-5" />
                                    Sightseeing Packages
                                </div>
                                <Button
                                    onClick={() => {
                                        if (sightSeeing.length > 0) {
                                            setSearchTerm("");
                                            setShowSightSeeingModal(true);
                                        } else {
                                            addSightseeingPackage();
                                        }
                                    }}
                                    variant="outline"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Package
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {formData.sightseeing_packages.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No sightseeing packages added. Click "Add
                                    Package" to begin.
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {formData.sightseeing_packages.map(
                                        (pkg, pkgIdx) => (
                                            <div
                                                key={pkg.id}
                                                className="border-2 border-orange-200 rounded-lg p-6 bg-orange-50/30"
                                            >
                                                <div className="flex items-center justify-between mb-6">
                                                    <h3 className="text-xl font-bold text-orange-700">
                                                        Package {pkgIdx + 1}
                                                    </h3>
                                                    <Button
                                                        onClick={() =>
                                                            removeSightseeingPackage(
                                                                pkgIdx,
                                                            )
                                                        }
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-red-500 hover:bg-red-50"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>

                                                {/* Basic Package Information */}
                                                <div className="bg-white rounded-lg p-4 mb-4">
                                                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                                        <MapPin className="w-4 h-4" />
                                                        Basic Information
                                                    </h4>
                                                    <div className="grid grid-cols-3 gap-4">
                                                        <div className="col-span-2">
                                                            <Label>
                                                                Package Name
                                                            </Label>
                                                            <Input
                                                                value={
                                                                    pkg.package_name
                                                                }
                                                                onChange={(e) =>
                                                                    updateSightseeingPackage(
                                                                        pkgIdx,
                                                                        "package_name",
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                placeholder="e.g., Complete Delhi Heritage Tour"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>City</Label>
                                                            <Input
                                                                value={pkg.city}
                                                                onChange={(e) =>
                                                                    updateSightseeingPackage(
                                                                        pkgIdx,
                                                                        "city",
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                placeholder="e.g., New Delhi"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>Date</Label>
                                                            <Input
                                                                type="date"
                                                                value={pkg.date}
                                                                onChange={(e) =>
                                                                    updateSightseeingPackage(
                                                                        pkgIdx,
                                                                        "date",
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                min={
                                                                    new Date()
                                                                        .toISOString()
                                                                        .split(
                                                                            "T",
                                                                        )[0]
                                                                }
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>
                                                                Start Time
                                                            </Label>
                                                            <Input
                                                                type="time"
                                                                value={
                                                                    pkg.start_time
                                                                }
                                                                onChange={(e) =>
                                                                    updateSightseeingPackage(
                                                                        pkgIdx,
                                                                        "start_time",
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                placeholder="e.g., 08:00 AM"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>
                                                                End Time
                                                            </Label>
                                                            <Input
                                                                type="time"
                                                                value={
                                                                    pkg.end_time
                                                                }
                                                                onChange={(e) =>
                                                                    updateSightseeingPackage(
                                                                        pkgIdx,
                                                                        "end_time",
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                placeholder="e.g., 06:00 PM"
                                                            />
                                                        </div>
                                                        <div className="col-span-3">
                                                            <Label>
                                                                Duration (text)
                                                            </Label>
                                                            <Input
                                                                value={
                                                                    pkg.duration
                                                                }
                                                                onChange={(e) =>
                                                                    updateSightseeingPackage(
                                                                        pkgIdx,
                                                                        "duration",
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                placeholder="e.g., 10 hours or Full Day"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Places to Visit */}
                                                <div className="bg-white rounded-lg p-4 mb-4">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                                                            <ActivityIcon className="w-4 h-4" />
                                                            Places to Visit
                                                        </h4>
                                                        <Button
                                                            onClick={() =>
                                                                addPlaceToPackage(
                                                                    pkgIdx,
                                                                )
                                                            }
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                                        >
                                                            <Plus className="w-4 h-4 mr-1" />
                                                            Add Place
                                                        </Button>
                                                    </div>

                                                    {pkg.places &&
                                                        pkg.places.length === 0 ? (
                                                        <div className="text-center py-6 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                                                            No places added.
                                                            Click "Add Place" to
                                                            add sightseeing
                                                            destinations.
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-3">
                                                            {pkg.places &&
                                                                pkg.places.map(
                                                                    (
                                                                        place,
                                                                        placeIdx,
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                placeIdx
                                                                            }
                                                                            className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                                                                        >
                                                                            <div className="flex items-center justify-between mb-3">
                                                                                <span className="font-medium text-sm text-gray-700">
                                                                                    Place{" "}
                                                                                    {placeIdx +
                                                                                        1}
                                                                                </span>
                                                                                <Button
                                                                                    onClick={() =>
                                                                                        removePlaceFromPackage(
                                                                                            pkgIdx,
                                                                                            placeIdx,
                                                                                        )
                                                                                    }
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    className="text-red-500 hover:bg-red-50 h-8"
                                                                                >
                                                                                    <Trash2 className="w-3 h-3" />
                                                                                </Button>
                                                                            </div>
                                                                            <div className="grid grid-cols-2 gap-3">
                                                                                <div>
                                                                                    <Label className="text-xs">
                                                                                        Place
                                                                                        Name
                                                                                    </Label>
                                                                                    <Input
                                                                                        value={
                                                                                            place.name
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updatePlaceInPackage(
                                                                                                pkgIdx,
                                                                                                placeIdx,
                                                                                                "name",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                        placeholder="e.g., Red Fort (Lal Qila)"
                                                                                        className="h-9"
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <Label className="text-xs">
                                                                                        Duration
                                                                                    </Label>
                                                                                    <Input
                                                                                        value={
                                                                                            place.duration
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updatePlaceInPackage(
                                                                                                pkgIdx,
                                                                                                placeIdx,
                                                                                                "duration",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                        placeholder="e.g., 1.5 hours"
                                                                                        className="h-9"
                                                                                    />
                                                                                </div>
                                                                                <div className="col-span-2">
                                                                                    <Label className="text-xs">
                                                                                        Image
                                                                                        URL
                                                                                    </Label>
                                                                                    <Input
                                                                                        value={
                                                                                            place.image
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updatePlaceInPackage(
                                                                                                pkgIdx,
                                                                                                placeIdx,
                                                                                                "image",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                        placeholder="https://example.com/image.jpg"
                                                                                        className="h-9"
                                                                                    />
                                                                                </div>
                                                                                <div className="col-span-2">
                                                                                    <Label className="text-xs">
                                                                                        Description
                                                                                    </Label>
                                                                                    <Textarea
                                                                                        value={
                                                                                            place.description
                                                                                        }
                                                                                        onChange={(
                                                                                            e,
                                                                                        ) =>
                                                                                            updatePlaceInPackage(
                                                                                                pkgIdx,
                                                                                                placeIdx,
                                                                                                "description",
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                            )
                                                                                        }
                                                                                        placeholder="Brief description of the place..."
                                                                                        rows="2"
                                                                                        className="text-sm"
                                                                                    />
                                                                                </div>
                                                                                <div className="col-span-2">
                                                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                                                        <Input
                                                                                            type="checkbox"
                                                                                            checked={
                                                                                                place.entry_fee_included
                                                                                            }
                                                                                            onChange={(
                                                                                                e,
                                                                                            ) =>
                                                                                                updatePlaceInPackage(
                                                                                                    pkgIdx,
                                                                                                    placeIdx,
                                                                                                    "entry_fee_included",
                                                                                                    e
                                                                                                        .target
                                                                                                        .checked,
                                                                                                )
                                                                                            }
                                                                                            className="w-4 h-4 text-orange-600 rounded"
                                                                                        />
                                                                                        <span className="text-sm text-gray-700">
                                                                                            Entry
                                                                                            fee
                                                                                            included
                                                                                        </span>
                                                                                    </label>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ),
                                                                )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Inclusions & Services */}
                                                <div className="bg-white rounded-lg p-4 mb-4">
                                                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                                        <Car className="w-4 h-4" />
                                                        Inclusions & Services
                                                    </h4>
                                                    <div className="space-y-4">
                                                        {/* Transport */}
                                                        <div className="border-b pb-3">
                                                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                                                                <Input
                                                                    type="checkbox"
                                                                    checked={
                                                                        pkg.transport_included
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        updateSightseeingPackage(
                                                                            pkgIdx,
                                                                            "transport_included",
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                        )
                                                                    }
                                                                    className="w-4 h-4 text-orange-600 rounded"
                                                                />
                                                                <span className="font-medium text-sm text-gray-700">
                                                                    Transport
                                                                    Included
                                                                </span>
                                                            </label>
                                                            {pkg.transport_included && (
                                                                <div>
                                                                    <Label className="text-xs">
                                                                        Transport
                                                                        Details
                                                                    </Label>
                                                                    <Input
                                                                        value={
                                                                            pkg.transport_details
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            updateSightseeingPackage(
                                                                                pkgIdx,
                                                                                "transport_details",
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }
                                                                        placeholder="e.g., Air-conditioned sedan (Toyota Etios / Similar) with experienced driver"
                                                                        className="h-9"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Guide */}
                                                        <div className="border-b pb-3">
                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                <Input
                                                                    type="checkbox"
                                                                    checked={
                                                                        pkg.guide_included
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        updateSightseeingPackage(
                                                                            pkgIdx,
                                                                            "guide_included",
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                        )
                                                                    }
                                                                    className="w-4 h-4 text-orange-600 rounded"
                                                                />
                                                                <span className="font-medium text-sm text-gray-700">
                                                                    Guide
                                                                    Included
                                                                </span>
                                                            </label>
                                                        </div>

                                                        {/* Meals */}
                                                        <div>
                                                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                                                                <Input
                                                                    type="checkbox"
                                                                    checked={
                                                                        pkg.meal_included
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        updateSightseeingPackage(
                                                                            pkgIdx,
                                                                            "meal_included",
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                        )
                                                                    }
                                                                    className="w-4 h-4 text-orange-600 rounded"
                                                                />
                                                                <span className="font-medium text-sm text-gray-700">
                                                                    Meal
                                                                    Included
                                                                </span>
                                                            </label>
                                                            {pkg.meal_included && (
                                                                <div>
                                                                    <Label className="text-xs">
                                                                        Meal
                                                                        Details
                                                                    </Label>
                                                                    <Input
                                                                        value={
                                                                            pkg.meal_details
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            updateSightseeingPackage(
                                                                                pkgIdx,
                                                                                "meal_details",
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }
                                                                        placeholder="e.g., Lunch at a popular local restaurant (Vegetarian and Non-vegetarian options)"
                                                                        className="h-9"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Pricing & Notes */}
                                                <div className="bg-white rounded-lg p-4">
                                                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                                        <DollarSign className="w-4 h-4" />
                                                        Pricing & Notes
                                                    </h4>
                                                    <div className="grid grid-cols-4 gap-4">
                                                        <div>
                                                            <Label>
                                                                Number of People
                                                            </Label>
                                                            <Input
                                                                type="number"
                                                                value={
                                                                    pkg.number_of_people
                                                                }
                                                                onChange={(e) =>
                                                                    updateSightseeingPackage(
                                                                        pkgIdx,
                                                                        "number_of_people",
                                                                        parseInt(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        ),
                                                                    )
                                                                }
                                                                className="h-9"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>
                                                                Cost Per Person
                                                            </Label>
                                                            <Input
                                                                type="number"
                                                                value={
                                                                    pkg.cost_per_person
                                                                }
                                                                onChange={(e) =>
                                                                    updateSightseeingPackage(
                                                                        pkgIdx,
                                                                        "cost_per_person",
                                                                        parseFloat(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        ),
                                                                    )
                                                                }
                                                                className="h-9"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>GST %</Label>
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                value={
                                                                    pkg.gst_percentage
                                                                }
                                                                onChange={(e) =>
                                                                    updateSightseeingPackage(
                                                                        pkgIdx,
                                                                        "gst_percentage",
                                                                        parseFloat(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        ),
                                                                    )
                                                                }
                                                                className="h-9"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>
                                                                Total Cost
                                                                (Auto-calculated)
                                                            </Label>
                                                            <Input
                                                                type="number"
                                                                value={
                                                                    pkg.total_cost
                                                                }
                                                                disabled
                                                                className="h-9 bg-gray-100"
                                                            />
                                                        </div>
                                                        <div className="col-span-4">
                                                            <Label>Notes</Label>
                                                            <Textarea
                                                                value={
                                                                    pkg.notes
                                                                }
                                                                onChange={(e) =>
                                                                    updateSightseeingPackage(
                                                                        pkgIdx,
                                                                        "notes",
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                placeholder="e.g., Comfortable walking shoes recommended. Friday is closed for Jama Masjid."
                                                                rows="2"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Train Section */}
                {selectedCategories.includes("Train") && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Train className="w-5 h-5" />
                                    Train Details
                                </div>
                                <Button
                                    onClick={addTrain}
                                    variant="outline"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Train
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {formData.trains.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No trains added. Click "Add Train" to begin.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {formData.trains.map((train, trainIdx) => (
                                        <div
                                            key={train.id}
                                            className="border rounded-lg p-4"
                                        >
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-lg font-semibold">
                                                    Train {trainIdx + 1}
                                                </h3>
                                                <Button
                                                    onClick={() =>
                                                        removeTrain(trainIdx)
                                                    }
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-red-500"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>

                                            <div className="grid grid-cols-3 gap-4 mb-4">
                                                <div>
                                                    <Label>
                                                        Total Passengers
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        value={
                                                            train.total_passengers
                                                        }
                                                        onChange={(e) => {
                                                            updateTrainField(
                                                                trainIdx,
                                                                "total_passengers",
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            );
                                                        }}
                                                        min="1"
                                                    />
                                                </div>

                                                <div>
                                                    <Label>
                                                        Cost Per Person
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        value={
                                                            train.cost_per_person
                                                        }
                                                        onChange={(e) => {
                                                            updateTrainField(
                                                                trainIdx,
                                                                "cost_per_person",
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            );
                                                        }}
                                                        min="0"
                                                    />
                                                </div>
                                                <div>
                                                    <Label>
                                                        Total Cost
                                                        (Auto-calculated)
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        value={train.total_cost}
                                                        disabled
                                                    />
                                                </div>
                                                <div>
                                                    <Label>GST %</Label>
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={
                                                            train.gst_percentage
                                                        }
                                                        onChange={(e) => {
                                                            updateTrainField(
                                                                trainIdx,
                                                                "gst_percentage",
                                                                parseFloat(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            );
                                                        }}
                                                        min="0"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <Label className="text-base font-semibold">
                                                        Train Segments
                                                    </Label>
                                                    <Button
                                                        onClick={() =>
                                                            addTrainSegment(
                                                                trainIdx,
                                                            )
                                                        }
                                                        variant="outline"
                                                        size="sm"
                                                    >
                                                        <Plus className="w-4 h-4 mr-1" />
                                                        Add Segment
                                                    </Button>
                                                </div>
                                                {train.segments.map(
                                                    (segment, segIdx) => (
                                                        <div
                                                            key={segIdx}
                                                            className="bg-gray-50 p-4 rounded-lg mb-3 space-y-4"
                                                        >
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                                                <div>
                                                                    <Label>Train Number</Label>
                                                                    <Input
                                                                        placeholder="Train Number"
                                                                        value={
                                                                            segment.train_number
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].train_number =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <Label>Train Name</Label>
                                                                    <Input
                                                                        placeholder="Train Name"
                                                                        value={
                                                                            segment.train_name
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].train_name =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <Label>Class Type</Label>
                                                                    <Input
                                                                        placeholder="Class Type"
                                                                        value={
                                                                            segment.class_type
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].class_type =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <Label>Duration</Label>
                                                                    <Input
                                                                        placeholder="e.g. 2H 30M"
                                                                        value={
                                                                            segment.duration
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].duration =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {/* Departure */}
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                                                <div>
                                                                    <Label>Departure City</Label>
                                                                    <Input
                                                                        placeholder="Departure City"
                                                                        value={
                                                                            segment.departure_city
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].departure_city =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <Label>Departure Station</Label>
                                                                    <Input
                                                                        placeholder="Departure Station"
                                                                        value={
                                                                            segment.departure_station
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].departure_station =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <Label>Arrival City</Label>
                                                                    <Input
                                                                        placeholder="Arrival City"
                                                                        value={
                                                                            segment.arrival_city
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].arrival_city =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <Label>Arrival Station</Label>
                                                                    <Input
                                                                        placeholder="Arrival Station"
                                                                        value={
                                                                            segment.arrival_station
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].arrival_station =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {/* Arrival */}
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                                                <div>
                                                                    <Label>
                                                                        Departure
                                                                        Date
                                                                    </Label>
                                                                    <Input
                                                                        type="date"
                                                                        value={
                                                                            segment.departure_date
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].departure_date =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                        min={
                                                                            new Date()
                                                                                .toISOString()
                                                                                .split(
                                                                                    "T",
                                                                                )[0]
                                                                        }
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <Label>
                                                                        Departure
                                                                        Time
                                                                    </Label>
                                                                    <Input
                                                                        type="time"
                                                                        value={
                                                                            segment.departure_time
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].departure_time =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <Label>
                                                                        Arrival
                                                                        Date
                                                                    </Label>
                                                                    <Input
                                                                        type="date"
                                                                        value={
                                                                            segment.arrival_date
                                                                        }
                                                                        min={
                                                                            segment.departure_date
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].arrival_date =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                        disabled={
                                                                            !segment.departure_date
                                                                        }
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <Label>
                                                                        Arrival
                                                                        Time
                                                                    </Label>
                                                                    <Input
                                                                        type="time"
                                                                        value={
                                                                            segment.arrival_time
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated =
                                                                                [
                                                                                    ...formData.trains,
                                                                                ];
                                                                            updated[
                                                                                trainIdx
                                                                            ].segments[
                                                                                segIdx
                                                                            ].arrival_time =
                                                                                e.target.value;
                                                                            setFormData(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    trains: updated,
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Extra info */}
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                                                            </div>
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                            <div className="mb-4">
                                                <Label>Notes</Label>
                                                <Textarea
                                                    value={train.notes}
                                                    onChange={(e) => {
                                                        updateTrainField(
                                                            trainIdx,
                                                            "notes",
                                                            e.target.value,
                                                        );
                                                    }}
                                                    rows="2"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Bus Section */}
                {selectedCategories.includes("Bus") && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Bus className="w-5 h-5" />
                                    Bus Details
                                </div>
                                <Button
                                    onClick={addBus}
                                    variant="outline"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Bus
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {formData.buses.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No buses added. Click "Add Bus" to begin.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {formData.buses.map((bus, busIdx) => (
                                        <div
                                            key={bus.id}
                                            className="border rounded-lg p-4"
                                        >
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-lg font-semibold">
                                                    Bus {busIdx + 1}
                                                </h3>
                                                <Button
                                                    onClick={() =>
                                                        removeBus(busIdx)
                                                    }
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-red-500"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <Label>
                                                        Total Passengers
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        value={
                                                            bus.total_passengers
                                                        }
                                                        onChange={(e) => {
                                                            const updated = [
                                                                ...formData.buses,
                                                            ];
                                                            updated[
                                                                busIdx
                                                            ].total_passengers =
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                );
                                                            setFormData(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    buses: updated,
                                                                }),
                                                            );
                                                        }}
                                                        min="0"
                                                    />
                                                </div>
                                                <div>
                                                    <Label>GST %</Label>
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={
                                                            bus.gst_percentage
                                                        }
                                                        onChange={(e) => {
                                                            const updated = [
                                                                ...formData.buses,
                                                            ];
                                                            updated[
                                                                busIdx
                                                            ].gst_percentage =
                                                                parseFloat(
                                                                    e.target
                                                                        .value,
                                                                );
                                                            setFormData(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    buses: updated,
                                                                }),
                                                            );
                                                        }}
                                                        min="0"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <Label className="text-base font-semibold">
                                                        Bus Segments
                                                    </Label>
                                                    <Button
                                                        onClick={() =>
                                                            addBusSegment(
                                                                busIdx,
                                                            )
                                                        }
                                                        variant="outline"
                                                        size="sm"
                                                    >
                                                        <Plus className="w-4 h-4 mr-1" />
                                                        Add Segment
                                                    </Button>
                                                </div>

                                                {bus.segments.map(
                                                    (segment, segIdx) => (
                                                        <div
                                                            key={segIdx}
                                                            className="bg-gray-50 p-3 rounded-lg mb-2"
                                                        >
                                                            {" "}
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                                <div>
                                                                    <Label>
                                                                        Bus
                                                                        Operator
                                                                    </Label>
                                                                    <Input
                                                                        value={
                                                                            segment.bus_operator
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            updateBusSegment(
                                                                                busIdx,
                                                                                segIdx,
                                                                                "bus_operator",
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }
                                                                        placeholder="e.g. Volvo Travels"
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <Label>
                                                                        Bus Type
                                                                    </Label>
                                                                    <Input
                                                                        value={
                                                                            segment.bus_type
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            updateBusSegment(
                                                                                busIdx,
                                                                                segIdx,
                                                                                "bus_type",
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }
                                                                        placeholder="AC Sleeper"
                                                                    />
                                                                </div>
                                                            </div>
                                                            {/* Departure Section */}
                                                            <div className="border-t pt-4 mt-2">
                                                                <h4 className="text-sm font-semibold text-gray-600 mb-3">
                                                                    Departure
                                                                    Details
                                                                </h4>

                                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                                    <div>
                                                                        <Label>Departure Location</Label>
                                                                        <Input
                                                                            placeholder="Departure Location"
                                                                            value={
                                                                                segment.departure_location
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) =>
                                                                                updateBusSegment(
                                                                                    busIdx,
                                                                                    segIdx,
                                                                                    "departure_location",
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>

                                                                    <div>
                                                                        <Label>Departure Date</Label>
                                                                        <Input
                                                                            type="date"
                                                                            value={
                                                                                segment.departure_date
                                                                            }
                                                                            min={
                                                                                new Date()
                                                                                    .toISOString()
                                                                                    .split(
                                                                                        "T",
                                                                                    )[0]
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) =>
                                                                                updateBusSegment(
                                                                                    busIdx,
                                                                                    segIdx,
                                                                                    "departure_date",
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>

                                                                    <div>
                                                                        <Label>Departure Time</Label>
                                                                        <Input
                                                                            type="time"
                                                                            value={
                                                                                segment.departure_time
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) =>
                                                                                updateBusSegment(
                                                                                    busIdx,
                                                                                    segIdx,
                                                                                    "departure_time",
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {/* Arrival Section */}
                                                            <div className="border-t pt-4 mt-4">
                                                                <h4 className="text-sm font-semibold text-gray-600 mb-3">
                                                                    Arrival
                                                                    Details
                                                                </h4>

                                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                                    <div>
                                                                        <Label>Arrival Location</Label>
                                                                        <Input
                                                                            placeholder="Arrival Location"
                                                                            value={
                                                                                segment.arrival_location
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) =>
                                                                                updateBusSegment(
                                                                                    busIdx,
                                                                                    segIdx,
                                                                                    "arrival_location",
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>

                                                                    <div>
                                                                        <Label>Arrival Date</Label>
                                                                        <Input
                                                                            type="date"
                                                                            value={
                                                                                segment.arrival_date
                                                                            }
                                                                            min={
                                                                                segment.departure_date
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) =>
                                                                                updateBusSegment(
                                                                                    busIdx,
                                                                                    segIdx,
                                                                                    "arrival_date",
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>

                                                                    <div>
                                                                        <Label>Arrival Time</Label>
                                                                        <Input
                                                                            type="time"
                                                                            value={
                                                                                segment.arrival_time
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) =>
                                                                                updateBusSegment(
                                                                                    busIdx,
                                                                                    segIdx,
                                                                                    "arrival_time",
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>

                                                                    <div>
                                                                        <Label>Duration</Label>
                                                                        <Input
                                                                            placeholder="e.g. 2H 30M"
                                                                            value={
                                                                                segment.duration
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) =>
                                                                                updateBusSegment(
                                                                                    busIdx,
                                                                                    segIdx,
                                                                                    "duration",
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ),
                                                )}

                                                <div className="mt-4">
                                                    <Label>Total Cost</Label>
                                                    <Input
                                                        value={bus.total_cost}
                                                        onChange={(e) => {
                                                            const updated = [
                                                                ...formData.buses,
                                                            ];
                                                            ((updated[
                                                                busIdx
                                                            ].total_cost =
                                                                e.target.value),
                                                                setFormData(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        buses: updated,
                                                                    }),
                                                                ));
                                                        }}
                                                        placeholder="Additional information..."
                                                        rows={2}
                                                    />
                                                </div>
                                                {/* Notes */}
                                                <div className="mt-4">
                                                    <Label>Notes</Label>
                                                    <Textarea
                                                        value={bus.notes}
                                                        onChange={(e) => {
                                                            const updated = [
                                                                ...formData.buses,
                                                            ];
                                                            ((updated[
                                                                busIdx
                                                            ].notes =
                                                                e.target.value),
                                                                setFormData(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        buses: updated,
                                                                    }),
                                                                ));
                                                        }}
                                                        placeholder="Additional information..."
                                                        rows={2}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Day-by-Day Itinerary - Only for Holiday Packages */}
                {selectedCategories.includes("Holiday") && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-5 h-5" />
                                    Day-by-Day Itinerary (Holiday Package)
                                </div>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {formData.days.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    Select start and end dates to auto-generate
                                    itinerary days.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {formData.days.map((day, dayIndex) => (
                                        <div
                                            key={dayIndex}
                                            className="border rounded-lg p-4"
                                        >
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-lg font-semibold">
                                                    Day {day.dayNumber}
                                                </h3>
                                                <Button
                                                    onClick={() =>
                                                        removeDay(dayIndex)
                                                    }
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>

                                            <div className="grid grid-cols-3 gap-4 mb-4">
                                                <div>
                                                    <Label>Date</Label>
                                                    <Input
                                                        type="date"
                                                        value={day.date}
                                                        disabled={true}
                                                    />
                                                </div>
                                                <div>
                                                    <Label>Location</Label>
                                                    <Input
                                                        value={day.location}
                                                        onChange={(e) =>
                                                            updateDay(
                                                                dayIndex,
                                                                "location",
                                                                e.target.value,
                                                            )
                                                        }
                                                        placeholder="e.g., Srinagar"
                                                    />
                                                </div>
                                                <div>
                                                    <Label>
                                                        Hotel (If Needed)
                                                    </Label>
                                                    <br />
                                                    {!day.hotel && (
                                                        <Button
                                                            onClick={() => {
                                                                setSelectedDay(
                                                                    dayIndex,
                                                                );
                                                                setShowHotelModal(
                                                                    true,
                                                                );
                                                            }}
                                                            variant="outline"
                                                            className="w-full flex items-center justify-center"
                                                        >
                                                            <Plus className="w-4 h-4 mr-1" />
                                                            Select Hotel
                                                        </Button>
                                                    )}
                                                    {day.hotel && (
                                                        <div className="w-full h-9 border rounded-md px-3 flex items-center justify-between">
                                                            <div className="text-sm">
                                                                <span className="truncate">
                                                                    {
                                                                        day
                                                                            .hotel
                                                                            .name
                                                                    }
                                                                    <span className="text-yellow-500 ml-1">
                                                                        {"★".repeat(
                                                                            day
                                                                                .hotel
                                                                                .stars,
                                                                        )}
                                                                    </span>
                                                                </span>
                                                            </div>
                                                            <Button
                                                                onClick={() =>
                                                                    updateDay(
                                                                        dayIndex,
                                                                        "hotel",
                                                                        null,
                                                                    )
                                                                }
                                                                variant="ghost"
                                                                size="icon"
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mb-4">
                                                <Label className="mb-2 block">
                                                    Meals
                                                </Label>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <label className="font-medium text-xs text-gray-500">
                                                        Breakfast
                                                    </label>
                                                    <label className="font-medium text-xs text-gray-500">
                                                        Lunch
                                                    </label>
                                                    <label className="font-medium text-xs text-gray-500">
                                                        Dinner
                                                    </label>
                                                    <select
                                                        value={
                                                            day.meals.breakfast
                                                        }
                                                        onChange={(e) =>
                                                            updateDay(
                                                                dayIndex,
                                                                "meals",
                                                                {
                                                                    ...day.meals,
                                                                    breakfast:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            )
                                                        }
                                                        className="px-3 py-2 border rounded-lg bg-white"
                                                    >
                                                        <option>
                                                            Not Included
                                                        </option>
                                                        <option>
                                                            Included
                                                        </option>
                                                        <option
                                                            disabled={
                                                                !day.hotel
                                                            }
                                                        >
                                                            At Hotel
                                                        </option>
                                                    </select>
                                                    <select
                                                        value={day.meals.lunch}
                                                        onChange={(e) =>
                                                            updateDay(
                                                                dayIndex,
                                                                "meals",
                                                                {
                                                                    ...day.meals,
                                                                    lunch: e
                                                                        .target
                                                                        .value,
                                                                },
                                                            )
                                                        }
                                                        className="px-3 py-2 border rounded-lg bg-white"
                                                    >
                                                        <option>
                                                            Not Included
                                                        </option>
                                                        <option>
                                                            Included
                                                        </option>
                                                        <option
                                                            disabled={
                                                                !day.hotel
                                                            }
                                                        >
                                                            At Hotel
                                                        </option>
                                                    </select>
                                                    <select
                                                        value={day.meals.dinner}
                                                        onChange={(e) =>
                                                            updateDay(
                                                                dayIndex,
                                                                "meals",
                                                                {
                                                                    ...day.meals,
                                                                    dinner: e
                                                                        .target
                                                                        .value,
                                                                },
                                                            )
                                                        }
                                                        className="px-3 py-2 border rounded-lg bg-white"
                                                    >
                                                        <option>
                                                            Not Included
                                                        </option>
                                                        <option>
                                                            Included
                                                        </option>
                                                        <option
                                                            disabled={
                                                                !day.hotel
                                                            }
                                                        >
                                                            At Hotel
                                                        </option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <Label>Activities</Label>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            onClick={() => {
                                                                setSelectedDay(
                                                                    dayIndex,
                                                                );
                                                                setShowActivityModal(
                                                                    true,
                                                                );
                                                            }}
                                                            variant="outline"
                                                            size="sm"
                                                        >
                                                            <Plus className="w-4 h-4 mr-1" />
                                                            Add Activity
                                                        </Button>
                                                    </div>
                                                </div>

                                                {day.activities.length === 0 ? (
                                                    <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                                                        No activities added
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2">
                                                        {day.activities.map(
                                                            (
                                                                activity,
                                                                actIndex,
                                                            ) => (
                                                                <div
                                                                    key={
                                                                        actIndex
                                                                    }
                                                                    className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg"
                                                                >
                                                                    <div className="flex-1">
                                                                        {/* Main row */}
                                                                        <div className="grid grid-cols-4 gap-2">
                                                                            <Input
                                                                                value={
                                                                                    activity.time
                                                                                }
                                                                                onChange={(
                                                                                    e,
                                                                                ) => {
                                                                                    const updatedActivities =
                                                                                        [
                                                                                            ...day.activities,
                                                                                        ];
                                                                                    updatedActivities[
                                                                                        actIndex
                                                                                    ].time =
                                                                                        e.target.value;
                                                                                    updateDay(
                                                                                        dayIndex,
                                                                                        "activities",
                                                                                        updatedActivities,
                                                                                    );
                                                                                }}
                                                                                placeholder="Time"
                                                                            />

                                                                            <Input
                                                                                value={
                                                                                    activity.title
                                                                                }
                                                                                onChange={(
                                                                                    e,
                                                                                ) => {
                                                                                    const updatedActivities =
                                                                                        [
                                                                                            ...day.activities,
                                                                                        ];
                                                                                    updatedActivities[
                                                                                        actIndex
                                                                                    ].title =
                                                                                        e.target.value;
                                                                                    updateDay(
                                                                                        dayIndex,
                                                                                        "activities",
                                                                                        updatedActivities,
                                                                                    );
                                                                                }}
                                                                                placeholder="Activity title"
                                                                            />

                                                                            <Input
                                                                                value={
                                                                                    activity.meetingPoint
                                                                                }
                                                                                onChange={(
                                                                                    e,
                                                                                ) => {
                                                                                    const updatedActivities =
                                                                                        [
                                                                                            ...day.activities,
                                                                                        ];
                                                                                    updatedActivities[
                                                                                        actIndex
                                                                                    ].meetingPoint =
                                                                                        e.target.value;
                                                                                    updateDay(
                                                                                        dayIndex,
                                                                                        "activities",
                                                                                        updatedActivities,
                                                                                    );
                                                                                }}
                                                                                placeholder="Location / meeting point"
                                                                            />

                                                                            <Input
                                                                                value={
                                                                                    activity.image
                                                                                }
                                                                                onChange={(
                                                                                    e,
                                                                                ) => {
                                                                                    const updatedActivities =
                                                                                        [
                                                                                            ...day.activities,
                                                                                        ];
                                                                                    updatedActivities[
                                                                                        actIndex
                                                                                    ].image =
                                                                                        e.target.value;
                                                                                    updateDay(
                                                                                        dayIndex,
                                                                                        "activities",
                                                                                        updatedActivities,
                                                                                    );
                                                                                }}
                                                                                placeholder="Image URL (optional)"
                                                                            />
                                                                        </div>

                                                                        {/* Description */}
                                                                        <Textarea
                                                                            value={
                                                                                activity.description
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) => {
                                                                                const updatedActivities =
                                                                                    [
                                                                                        ...day.activities,
                                                                                    ];
                                                                                updatedActivities[
                                                                                    actIndex
                                                                                ].description =
                                                                                    e.target.value;
                                                                                updateDay(
                                                                                    dayIndex,
                                                                                    "activities",
                                                                                    updatedActivities,
                                                                                );
                                                                            }}
                                                                            placeholder="Description"
                                                                            className="mt-2"
                                                                            rows={
                                                                                2
                                                                            }
                                                                        />
                                                                    </div>

                                                                    <Button
                                                                        onClick={() =>
                                                                            removeActivityFromDay(
                                                                                dayIndex,
                                                                                actIndex,
                                                                            )
                                                                        }
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="text-red-500"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    {costBreakup.length > 0 && (
                                        <div className="mb-4">
                                            <Label className="mb-2 block">
                                                Cost Breakup (Only for
                                                Calculation)
                                            </Label>
                                            <div className="max-h-48 overflow-y-auto border rounded-lg">
                                                <table className="w-full table-auto">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            <th className="px-4 py-2 text-left">
                                                                Item
                                                            </th>
                                                            <th className="px-4 py-2 text-right">
                                                                Date
                                                            </th>
                                                            <th className="px-4 py-2 text-right">
                                                                Quantity
                                                            </th>
                                                            <th className="px-4 py-2 text-right">
                                                                Unit Cost
                                                            </th>
                                                            <th className="px-4 py-2 text-right">
                                                                Total
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {costBreakup.map(
                                                            (item) => (
                                                                <tr
                                                                    key={
                                                                        item.id
                                                                    }
                                                                    className="border-t"
                                                                >
                                                                    <td className="px-4 py-2">
                                                                        <Input
                                                                            value={item.name}
                                                                            onChange={(e) => {
                                                                                const updatedCostBreakup =
                                                                                    [
                                                                                        ...costBreakup,
                                                                                    ];
                                                                                updatedCostBreakup[
                                                                                    costBreakup.indexOf(
                                                                                        item,
                                                                                    )
                                                                                ].name =
                                                                                    e.target.value;
                                                                                setCostBreakup(
                                                                                    updatedCostBreakup,
                                                                                );
                                                                            }}
                                                                        />
                                                                    </td>
                                                                    <td className="px-4 py-2 text-right">
                                                                        {
                                                                            item.date
                                                                        }
                                                                    </td>
                                                                    <td className="px-4 py-2 text-right">
                                                                        <Input
                                                                            type="number"
                                                                            value={
                                                                                item.quantity ||
                                                                                0
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) => {
                                                                                const updatedCostBreakup =
                                                                                    [
                                                                                        ...costBreakup,
                                                                                    ];
                                                                                updatedCostBreakup[
                                                                                    costBreakup.indexOf(
                                                                                        item,
                                                                                    )
                                                                                ].quantity =
                                                                                    parseFloat(
                                                                                        e
                                                                                            .target
                                                                                            .value,
                                                                                    );
                                                                                setCostBreakup(
                                                                                    updatedCostBreakup,
                                                                                );
                                                                            }}
                                                                        />
                                                                    </td>
                                                                    <td className="px-4 py-2 text-right">
                                                                        <Input
                                                                            type="number"
                                                                            value={
                                                                                item.unit_cost
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) => {
                                                                                const updatedCostBreakup =
                                                                                    [
                                                                                        ...costBreakup,
                                                                                    ];
                                                                                updatedCostBreakup[
                                                                                    costBreakup.indexOf(
                                                                                        item,
                                                                                    )
                                                                                ].unit_cost =
                                                                                    parseFloat(
                                                                                        e
                                                                                            .target
                                                                                            .value,
                                                                                    );
                                                                                setCostBreakup(
                                                                                    updatedCostBreakup,
                                                                                );
                                                                            }}
                                                                        />
                                                                    </td>
                                                                    <td className="px-4 py-2 text-right">
                                                                        {(
                                                                            item.quantity *
                                                                            item.unit_cost
                                                                        ).toFixed(
                                                                            2,
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ),
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Pricing */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5" />
                            Final Pricing
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <Label>Air Cost</Label>
                                <Input value={Number(formData.pricing.airCost || 0).toFixed(2)} disabled className="bg-gray-100" />
                            </div>
                            <div>
                                <Label>Visa Cost</Label>
                                <Input value={Number(formData.pricing.visaCost || 0).toFixed(2)} disabled className="bg-gray-100" />
                            </div>
                            <div>
                                <Label>Railway Cost</Label>
                                <Input value={Number(formData.pricing.railwayCost || 0).toFixed(2)} disabled className="bg-gray-100" />
                            </div>
                            <div>
                                <Label>Package Cost</Label>
                                <Input value={Number(formData.pricing.packageCost || 0).toFixed(2)} disabled className="bg-gray-100" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                            <div>
                                <Label htmlFor="discount">Discount</Label>
                                <Input
                                    id="discount"
                                    type="number"
                                    value={formData.pricing.discount}
                                    onChange={(e) => handleInputChange("pricing", "discount", parseFloat(e.target.value))}
                                    min="0"
                                />
                            </div>
                            {/* <div>
                                <Label>TCS Percentage</Label>
                                <Input
                                    type="number"
                                    value={formData.pricing.tcsPercentage}
                                    onChange={(e) => handleInputChange("pricing", "tcsPercentage", parseFloat(e.target.value))}
                                />
                            </div> */}
                            {/* <div>
                                <Label>Taxes</Label>
                                <Input
                                    type="number"
                                    value={formData.pricing.taxes}
                                    onChange={(e) => handleInputChange("pricing", "taxes", parseFloat(e.target.value))}
                                />
                            </div> */}
                            <div>
                                <Label htmlFor="advance_payment_input">Advance Payment</Label>
                                <Input
                                    id="advance_payment_input"
                                    value={formData.pricing.depositDue}
                                    onChange={(e) => handleInputChange("pricing", "depositDue", e.target.value)}
                                />
                            </div>
                            <div>
                                <Label>Per Person <span className="text-gray-500 font-normal text-xs">({formData.summary.travelers} {formData.summary.travelers === 1 ? 'person' : 'persons'})</span></Label>
                                <Input value={Number(formData.pricing.perPerson || 0).toFixed(2)} disabled className="bg-gray-100" />
                                <p className="text-[11px] text-gray-500 mt-1">Per person is average cost for {formData.summary.travelers} {formData.summary.travelers === 1 ? 'person' : 'persons'}</p>
                            </div>
                            <div>
                                <Label className="font-bold">Total</Label>
                                <Input value={Number(formData.pricing.total || 0).toFixed(2)} disabled className="bg-gray-100 font-bold" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Inclusions */}
                <Card>
                    <CardHeader>
                        <CardTitle>Inclusions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="flex gap-2">
                                <Input
                                    value={newInclusion}
                                    onChange={(e) =>
                                        setNewInclusion(e.target.value)
                                    }
                                    placeholder="Add inclusion item..."
                                    onKeyPress={(e) =>
                                        e.key === "Enter" && addInclusion()
                                    }
                                />
                                <Button
                                    type="button"
                                    onClick={addInclusion}
                                    className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add
                                </Button>
                            </div>

                            <div className="space-y-2">
                                {formData.inclusions.length === 0 ? (
                                    <div className="text-center py-4 text-gray-500">
                                        No inclusions added yet
                                    </div>
                                ) : (
                                    formData.inclusions.map((item, index) => (
                                        <div
                                            key={index}
                                            draggable
                                            onDragStart={() => handleDragStartInclusion(index)}
                                            onDragEnter={() => handleDragEnterInclusion(index)}
                                            onDragEnd={handleDragEndInclusion}
                                            onDragOver={(e) => e.preventDefault()}
                                            className={`flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg ${draggedInclusionIndex === index ? "opacity-50" : ""}`}
                                        >
                                            <div className="cursor-move text-green-600 hover:text-green-800">
                                                <GripVertical className="w-4 h-4" />
                                            </div>
                                            <span className="flex-1 text-green-800">
                                                ✓ {item}
                                            </span>
                                            <Button
                                                type="button"
                                                onClick={() =>
                                                    removeInclusion(index)
                                                }
                                                variant="outline"
                                                size="sm"
                                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Exclusions */}
                <Card>
                    <CardHeader>
                        <CardTitle>Exclusions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="flex gap-2">
                                <Input
                                    value={newExclusion}
                                    onChange={(e) =>
                                        setNewExclusion(e.target.value)
                                    }
                                    placeholder="Add exclusion item..."
                                    onKeyPress={(e) =>
                                        e.key === "Enter" && addExclusion()
                                    }
                                />
                                <Button
                                    type="button"
                                    onClick={addExclusion}
                                    className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add
                                </Button>
                            </div>

                            <div className="space-y-2">
                                {formData.exclusions.length === 0 ? (
                                    <div className="text-center py-4 text-gray-500">
                                        No exclusions added yet
                                    </div>
                                ) : (
                                    formData.exclusions.map((item, index) => (
                                        <div
                                            key={index}
                                            draggable
                                            onDragStart={() => handleDragStartExclusion(index)}
                                            onDragEnter={() => handleDragEnterExclusion(index)}
                                            onDragEnd={handleDragEndExclusion}
                                            onDragOver={(e) => e.preventDefault()}
                                            className={`flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg ${draggedExclusionIndex === index ? "opacity-50" : ""}`}
                                        >
                                            <div className="cursor-move text-red-600 hover:text-red-800">
                                                <GripVertical className="w-4 h-4" />
                                            </div>
                                            <span className="flex-1 text-red-800">
                                                ✗ {item}
                                            </span>
                                            <Button
                                                type="button"
                                                onClick={() =>
                                                    removeExclusion(index)
                                                }
                                                variant="outline"
                                                size="sm"
                                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Activity Catalog Modal */}
            {showActivityModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                Select Activity from Catalog
                            </h2>
                            <div className="flex items-center gap-4">
                                <Select
                                    value={activityFilter}
                                    onValueChange={setActivityFilter}
                                >
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue placeholder="Filter by type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="All">All Categories</SelectItem>
                                        <SelectItem value="Sightseeing">Sightseeing</SelectItem>
                                        <SelectItem value="Activity">Activity</SelectItem>
                                        <SelectItem value="Transport">Transfers</SelectItem>
                                        <SelectItem value="Flight">Flight</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Button
                                    onClick={() => openCreateCatalogModal("activity")}
                                    className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-1 rounded-xl"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4" /> Add New to Catalog
                                </Button>
                                <Button
                                    onClick={() => setShowActivityModal(false)}
                                    variant="outline"
                                    size="sm"
                                >
                                    Close
                                </Button>
                            </div>
                        </div>

                        <Input
                            type="text"
                            placeholder="Search by activity name or destination..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border rounded-md p-2 mb-4"
                        />

                        <div className="grid grid-cols-3 gap-4">
                            <div
                                key={"blank"}
                                className="border rounded-lg p-4 cursor-pointer hover:border-orange-500 transition-colors"
                                onClick={() => {
                                    addActivityFromCatalog(
                                        selectedDay,
                                        {},
                                    );
                                    setShowActivityModal(false);
                                }}
                            >
                                <img
                                    src="https://as2.ftcdn.net/v2/jpg/02/85/38/91/1000_F_285389134_BxlMvm2bm5RwQzWWPvMeJzkiY2D9hCij.jpg"
                                    alt="Activity Template"
                                    className="w-full h-32 object-cover rounded mb-2"
                                />
                                <h3 className="font-semibold">
                                    Create Activity
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Create a new activity from scratch
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    Any
                                </p>
                            </div>
                            {filteredActivities.map((item, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-4 cursor-pointer hover:border-orange-500 transition-colors"
                                    onClick={() => {
                                        addActivityFromCatalog(
                                            selectedDay,
                                            item,
                                        );
                                        setShowActivityModal(false);
                                    }}
                                >
                                    {item.base_data_json.image_url && (
                                        <img
                                            src={item.base_data_json.image_url}
                                            alt={item.title}
                                            className="w-full h-32 object-cover rounded mb-2"
                                        />
                                    )}
                                    <h3 className="font-semibold">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm text-gray-600 line-clamp-2">
                                        {item.description}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {item.destination}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {activities.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                                No activities in catalog. Add activities to
                                catalog first.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showAddMiceEventModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                Select MICE Event from Catalog
                            </h2>
                            <div className="flex items-center gap-4">
                                <Button
                                    onClick={() => openCreateCatalogModal("mice")}
                                    className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-1 rounded-xl"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4" /> Add New to Catalog
                                </Button>
                                <Button
                                    onClick={() => setShowAddMiceEventModal(false)}
                                    variant="outline"
                                    size="sm"
                                >
                                    Close
                                </Button>
                            </div>
                        </div>

                        <Input
                            type="text"
                            placeholder="Search by mice events..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border rounded-md p-2 mb-4"
                        />

                        <div className="grid grid-cols-3 gap-4">
                            {filteredMiceEvents.map((item, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-4 cursor-pointer hover:border-orange-500 transition-colors"
                                    onClick={() => {
                                        addMiceEventForCatalogIfExist(item);
                                        setShowAddMiceEventModal(false);
                                    }}
                                >
                                    {item.base_data_json.image_url && (
                                        <img
                                            src={item.base_data_json.image_url}
                                            alt={item.title}
                                            className="w-full h-32 object-cover rounded mb-2"
                                        />
                                    )}
                                    <h3 className="font-semibold">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {item.description}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {item.destination}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {activities.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                                No activities in catalog. Add activities to
                                catalog first.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showSightSeeingModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                Select Sightseeing from Catalog
                            </h2>
                            <div className="flex items-center gap-4">
                                <Button
                                    onClick={() => openCreateCatalogModal("sightseeing")}
                                    className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-1 rounded-xl"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4" /> Add New to Catalog
                                </Button>
                                <Button
                                    onClick={() => setShowSightSeeingModal(false)}
                                    variant="outline"
                                    size="sm"
                                >
                                    Close
                                </Button>
                            </div>
                        </div>

                        <Input
                            type="text"
                            placeholder="Search by sight seeing..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border rounded-md p-2 mb-4"
                        />

                        <div className="grid grid-cols-3 gap-4">
                            {filteredSightSeeing.map((item, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-4 cursor-pointer hover:border-orange-500 transition-colors"
                                    onClick={() => {
                                        addSightseeingPackageIfExistCatalog(
                                            item,
                                        );
                                        setShowSightSeeingModal(false);
                                    }}
                                >
                                    {item.base_data_json.image_url && (
                                        <img
                                            src={item.base_data_json.image_url}
                                            alt={item.title}
                                            className="w-full h-32 object-cover rounded mb-2"
                                        />
                                    )}
                                    <h3 className="font-semibold">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {item.description}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {item.destination}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showTransportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                Select Transport from Catalog
                            </h2>
                            <div className="flex items-center gap-4">
                                <Button
                                    onClick={() => openCreateCatalogModal("transport")}
                                    className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-1 rounded-xl"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4" /> Add New to Catalog
                                </Button>
                                <Button
                                    onClick={() => setShowTransportModal(false)}
                                    variant="outline"
                                    size="sm"
                                >
                                    Close
                                </Button>
                            </div>
                        </div>

                        <Input
                            type="text"
                            placeholder="Search by transport ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border rounded-md p-2 mb-4"
                        />

                        <div className="grid grid-cols-3 gap-4">
                            {filteredTransports.map((item, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-4 cursor-pointer hover:border-orange-500 transition-colors"
                                    onClick={() => {
                                        addTransportForCatalogIfExist(item);
                                        setShowTransportModal(false);
                                    }}
                                >
                                    {item.base_data_json.image_url && (
                                        <img
                                            src={item.base_data_json.image_url}
                                            alt={item.title}
                                            className="w-full h-32 object-cover rounded mb-2"
                                        />
                                    )}
                                    <h3 className="font-semibold">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {item.description}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {item.destination}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showFlightModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                Select Flight from Catalog
                            </h2>
                            <div className="flex items-center gap-4">
                                <Button
                                    onClick={() => openCreateCatalogModal("flight")}
                                    className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-1 rounded-xl"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4" /> Add New to Catalog
                                </Button>
                                <Button
                                    onClick={() => setShowFlightModal(false)}
                                    variant="outline"
                                    size="sm"
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                        <Input
                            type="text"
                            placeholder="Search by flight ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border rounded-md p-2 mb-4"
                        />
                        <div className="grid grid-cols-3 gap-4">
                            {filteredFlights.map((item, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-4 cursor-pointer hover:border-orange-500 transition-colors"
                                    onClick={() => {
                                        addFlightSegmentIfFlightCatalogExist(
                                            item,
                                        );
                                        setShowFlightModal(false);
                                    }}
                                >
                                    {item.base_data_json.image_url && (
                                        <img
                                            src={item.base_data_json.image_url}
                                            alt={item.title}
                                            className="w-full h-32 object-cover rounded mb-2"
                                        />
                                    )}
                                    <h3 className="font-semibold">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {item.description}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {item.destination}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showVisaModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                Select Visa from Catalog
                            </h2>
                            <div className="flex items-center gap-4">
                                <Button
                                    onClick={() => openCreateCatalogModal("visa")}
                                    className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-1 rounded-xl"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4" /> Add New to Catalog
                                </Button>
                                <Button
                                    onClick={() => setShowVisaModal(false)}
                                    variant="outline"
                                    size="sm"
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                        <Input
                            type="text"
                            placeholder="Search by Visa ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border rounded-md p-2 mb-4"
                        />
                        <div className="grid grid-cols-3 gap-4">
                            {filteredVisas.map((item, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-4 cursor-pointer hover:border-orange-500 transition-colors"
                                    onClick={() => {
                                        addVisaIfExistCatalog(item);
                                        setShowVisaModal(false);
                                    }}
                                >
                                    {item.base_data_json.image_url && (
                                        <img
                                            src={item.base_data_json.image_url}
                                            alt={item.title}
                                            className="w-full h-32 object-cover rounded mb-2"
                                        />
                                    )}
                                    <h3 className="font-semibold">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {item.description}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {item.destination}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {showAddHotelModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                Select Hotel from Catalog
                            </h2>
                            <div className="flex items-center gap-4">
                                <Button
                                    onClick={() => openCreateCatalogModal("hotel")}
                                    className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-1 rounded-xl"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4" /> Add New to Catalog
                                </Button>
                                <Button
                                    onClick={() => setShowAddHotelModal(false)}
                                    variant="outline"
                                    size="sm"
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                        <Input
                            type="text"
                            placeholder="Search by Hotel Name, Supplier, City, Destination or Address"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border rounded-md p-2 mb-4"
                        />
                        <div className="grid grid-cols-3 gap-4">
                            {filteredHotels.map((item, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-4 cursor-pointer hover:border-orange-500 transition-colors"
                                    onClick={() => {
                                        addStandaloneHotelIfCatalogExistForHotel(
                                            item,
                                        );
                                        setShowAddHotelModal(false);
                                    }}
                                >
                                    {item.base_data_json.image_url && (
                                        <img
                                            src={item.base_data_json.image_url}
                                            alt={item.title}
                                            className="w-full h-32 object-cover rounded mb-2"
                                        />
                                    )}
                                    <h3 className="font-semibold">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {item.description}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {item.destination}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {activities.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                                No activities in catalog. Add activities to
                                catalog first.
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* Hotel Catalog Modal */}
            {showHotelModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">
                                Select Hotel from Catalog
                            </h2>
                            <div className="flex items-center gap-4">
                                <Button
                                    onClick={() => openCreateCatalogModal("hotel")}
                                    className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-1 rounded-xl"
                                    size="sm"
                                >
                                    <Plus className="w-4 h-4" /> Add New to Catalog
                                </Button>
                                <Button
                                    onClick={() => setShowHotelModal(false)}
                                    variant="outline"
                                    size="sm"
                                >
                                    Close
                                </Button>
                            </div>
                        </div>

                        <Input
                            type="text"
                            placeholder="Search by hotel name or destination..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border rounded-md p-2 mb-4"
                        />

                        <div className="grid grid-cols-3 gap-4">
                            {filteredHotels.map((item, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-4 cursor-pointer hover:border-orange-500 transition-colors"
                                    onClick={() => {
                                        addHotelFromCatalog(selectedDay, item);
                                        setShowHotelModal(false);
                                    }}
                                >
                                    {item.base_data_json.image_url && (
                                        <img
                                            src={item.base_data_json.image_url}
                                            alt={item.title}
                                            className="w-full h-32 object-cover rounded mb-2"
                                        />
                                    )}
                                    <h3 className="font-semibold">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {item.description}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {item.destination}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {hotels.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                                No hotels in catalog. Add hotels to catalog
                                first.
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* Floating Calculator Button */}
            {/* <div className="fixed bottom-6 right-6 z-50">
                <button
                    onClick={() => setShowCurrencyPanel(!showCurrencyPanel)}
                    className="w-14 h-14 bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-orange-700 hover:scale-105 transition-all duration-200"
                >
                    <Calculator className="w-6 h-6" />
                </button>
            </div> */}
            {showCurrencyPanel && (
                <div className="fixed bottom-24 right-6 z-50 w-96 bg-white rounded-2xl shadow-2xl p-5 border">
                    {/* Close Button */}
                    <button
                        onClick={() => setShowCurrencyPanel(false)}
                        className="absolute top-3 right-3 text-gray-400 hover:text-red-500"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <h3 className="text-sm font-semibold mb-4 text-gray-700">
                        Currency Converter
                    </h3>

                    <div className="flex gap-4">
                        {/* LEFT SIDE (USD & INR stacked) */}
                        <div className="flex flex-col gap-4 w-1/2">
                            {/* USD Input */}
                            <div className="border rounded-lg px-4 py-3 flex items-center gap-2">
                                <DollarSign className="w-5 h-5 text-gray-500" />
                                <Input
                                    type="number"
                                    value={usdAmount}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setUsdAmount(value);
                                        setInrAmount(
                                            value
                                                ? (
                                                    value * conversionRate
                                                ).toFixed(2)
                                                : "",
                                        );
                                    }}
                                    placeholder="USD"
                                    className="w-full outline-none"
                                />
                            </div>

                            {/* INR Input */}
                            <div className="border rounded-lg px-4 py-3 flex items-center gap-2">
                                <IndianRupee className="w-5 h-5 text-green-600" />
                                <Input
                                    type="number"
                                    value={inrAmount}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setInrAmount(value);
                                        setUsdAmount(
                                            value
                                                ? (
                                                    value / conversionRate
                                                ).toFixed(2)
                                                : "",
                                        );
                                    }}
                                    placeholder="INR"
                                    className="w-full outline-none"
                                />
                            </div>
                        </div>

                        {/* RIGHT SIDE (Conversion Rate Box) */}
                        <div className="w-1/2 border rounded-lg px-4 py-3 flex flex-col justify-center items-center">
                            <span className="text-xs text-gray-500 mb-2">
                                Conversion Rate
                            </span>

                            <Input
                                type="number"
                                value={conversionRate}
                                onChange={(e) =>
                                    setConversionRate(Number(e.target.value))
                                }
                                className="w-24 text-center border rounded-md py-1 outline-none"
                            />

                            <span className="text-xs text-gray-400 mt-2">
                                1 USD = {conversionRate} INR
                            </span>
                        </div>
                    </div>
                </div>
            )}

            <Dialog open={showAdvanceWarning} onOpenChange={setShowAdvanceWarning}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Zero Advance Payment Confirmation</DialogTitle>
                        <DialogDescription className="pt-2">
                            The Advance Payment for this quotation is currently set to <strong>0</strong>.
                            Are you sure you want to proceed and save?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowAdvanceWarning(false);
                                setTimeout(() => {
                                    const el = document.getElementById("advance_payment_input");
                                    if (el) {
                                        el.focus();
                                        el.scrollIntoView({ behavior: "smooth", block: "center" });
                                    }
                                }, 100);
                            }}
                        >
                            Edit Payment
                        </Button>
                        <Button
                            onClick={() => {
                                setShowAdvanceWarning(false);
                                if (pendingSaveParams) {
                                    handleSave(pendingSaveParams.status, pendingSaveParams.expiry_date, true);
                                }
                            }}
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                        >
                            Confirm & Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Upload Cover Image Modal (integrated in Quotation Builder) */}
            <Dialog open={coverUploadOpen} onOpenChange={(open) => {
                if (!open) {
                    setCoverUploadOpen(false);
                    setNewImageName("");
                    setNewImageFile(null);
                    setNewImageUrlInput("");
                    setNewImageMode("file");
                }
            }}>
                <DialogContent className="sm:max-w-[460px] rounded-3xl p-6 bg-white shadow-2xl border-0">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                            <Upload className="w-5 h-5 text-orange-600" />
                            Upload & Use New Cover Image
                        </DialogTitle>
                        <DialogDescription className="text-gray-500 text-sm mt-1">
                            Choose a local file or provide an external link. The image will be saved to AWS S3 and selected automatically.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-5 py-2">
                        {/* Selector Tabs */}
                        <div className="flex border-b border-gray-100 mb-2">
                            <button
                                type="button"
                                className={`flex-1 pb-2 text-sm font-semibold text-center border-b-2 transition-all ${newImageMode === "file"
                                    ? "border-orange-600 text-orange-600"
                                    : "border-transparent text-gray-400 hover:text-gray-600"
                                    }`}
                                onClick={() => setNewImageMode("file")}
                            >
                                Local File
                            </button>
                            <button
                                type="button"
                                className={`flex-1 pb-2 text-sm font-semibold text-center border-b-2 transition-all ${newImageMode === "link"
                                    ? "border-orange-600 text-orange-600"
                                    : "border-transparent text-gray-400 hover:text-gray-600"
                                    }`}
                                onClick={() => setNewImageMode("link")}
                            >
                                External Link
                            </button>
                        </div>

                        {newImageMode === "file" ? (
                            <div className="space-y-2">
                                <Label className="text-sm font-bold text-gray-700">Choose Image File</Label>
                                <Input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setNewImageFile(file);
                                            if (!newImageName) {
                                                const baseName = file.name.split(".").slice(0, -1).join(".");
                                                setNewImageName(baseName.replace(/[-_]/g, " "));
                                            }
                                        }
                                    }}
                                    className="rounded-xl border-gray-200"
                                />
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <Label htmlFor="new-ext-url" className="text-sm font-bold text-gray-700">Image URL Link</Label>
                                <Input
                                    id="new-ext-url"
                                    type="url"
                                    placeholder="https://example.com/image.jpg"
                                    value={newImageUrlInput}
                                    onChange={(e) => setNewImageUrlInput(e.target.value)}
                                    className="rounded-xl border-gray-200"
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="new-image-name" className="text-sm font-bold text-gray-700">Image Display Name</Label>
                            <Input
                                id="new-image-name"
                                type="text"
                                placeholder="Enter a descriptive name"
                                value={newImageName}
                                onChange={(e) => setNewImageName(e.target.value)}
                                className="rounded-xl border-gray-200"
                            />
                        </div>
                    </div>

                    <DialogFooter className="pt-2 sm:justify-end gap-2 flex-row">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setCoverUploadOpen(false);
                                setNewImageName("");
                                setNewImageFile(null);
                                setNewImageUrlInput("");
                                setNewImageMode("file");
                            }}
                            className="rounded-xl flex-1 sm:flex-none"
                            disabled={newImageUploading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={async () => {
                                if (!newImageName.trim()) {
                                    toast.error("Please enter a name for the image.");
                                    return;
                                }
                                if (newImageMode === "file" && !newImageFile) {
                                    toast.error("Please choose a local image file.");
                                    return;
                                }
                                if (newImageMode === "link" && !newImageUrlInput.trim()) {
                                    toast.error("Please enter a valid image URL.");
                                    return;
                                }

                                try {
                                    setNewImageUploading(true);
                                    let res;
                                    if (newImageMode === "file") {
                                        res = await api.createCoverImage(newImageName, newImageFile, null);
                                    } else {
                                        res = await api.createCoverImage(newImageName, null, newImageUrlInput);
                                    }
                                    toast.success("Cover image uploaded and selected successfully!");

                                    const newImg = res.data;
                                    setCoverImagesList(prev => [newImg, ...prev]);
                                    handleInputChange(null, "coverImage", newImg.image_url);

                                    setCoverUploadOpen(false);
                                    setNewImageName("");
                                    setNewImageFile(null);
                                    setNewImageUrlInput("");
                                    setNewImageMode("file");
                                } catch (err) {
                                    console.error("Failed to upload cover image:", err);
                                    toast.error(err.response?.data?.detail || "Could not upload image to S3.");
                                } finally {
                                    setNewImageUploading(false);
                                }
                            }}
                            className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl flex-1 sm:flex-none"
                            disabled={newImageUploading}
                        >
                            {newImageUploading ? "Saving..." : "Upload & Use"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cover Image Picker Modal (visual grid selection) */}
            <Dialog open={coverPickerOpen} onOpenChange={(open) => {
                setCoverPickerOpen(open);
                if (open) {
                    setCoverPickerPage(1);
                    setCoverPickerSearch("");
                    setCoverPickerSearchVal("");
                }
            }}>
                <DialogContent className="sm:max-w-[700px] rounded-3xl p-6 bg-white shadow-2xl border-0">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                            <ImageIcon className="w-5 h-5 text-orange-600" />
                            Select Cover Image
                        </DialogTitle>
                        <DialogDescription className="text-gray-500 text-sm mt-1">
                            Click on any image from your uploaded S3 assets to set it as the cover page of your quotation.
                        </DialogDescription>
                    </DialogHeader>

                    {/* Search and Filters Bar inside Modal */}
                    <div className="flex gap-2 mb-4">
                        <Input
                            placeholder="Search cover images by name..."
                            value={coverPickerSearchVal}
                            onChange={(e) => setCoverPickerSearchVal(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setCoverPickerPage(1);
                                    setCoverPickerSearch(coverPickerSearchVal);
                                }
                            }}
                            className="rounded-xl border-gray-200"
                        />
                        <Button
                            onClick={() => {
                                setCoverPickerPage(1);
                                setCoverPickerSearch(coverPickerSearchVal);
                            }}
                            className="bg-gray-800 hover:bg-gray-900 text-white rounded-xl"
                        >
                            Search
                        </Button>
                        {coverPickerSearch && (
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setCoverPickerSearchVal("");
                                    setCoverPickerSearch("");
                                    setCoverPickerPage(1);
                                }}
                                className="rounded-xl border-gray-200"
                            >
                                Clear
                            </Button>
                        )}
                    </div>

                    {coverImagesList.length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-2xl text-center">
                            <div className="p-3 bg-orange-50 text-orange-600 rounded-full mb-3">
                                <ImageIcon className="w-8 h-8" />
                            </div>
                            <h4 className="text-base font-bold text-gray-800">
                                {coverPickerSearch ? "No matching cover images found" : "No cover images uploaded yet"}
                            </h4>
                            <p className="text-gray-500 text-xs mt-1 max-w-xs">
                                {coverPickerSearch
                                    ? `We couldn't find any images matching "${coverPickerSearch}". Try a different term or clear the filter.`
                                    : "Click the button below to upload a new cover image directly to S3."}
                            </p>
                            <div className="flex gap-3 mt-4">
                                {coverPickerSearch && (
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setCoverPickerSearchVal("");
                                            setCoverPickerSearch("");
                                            setCoverPickerPage(1);
                                        }}
                                        className="rounded-xl"
                                    >
                                        Clear Search
                                    </Button>
                                )}
                                <Button
                                    onClick={() => {
                                        setCoverPickerOpen(false);
                                        setCoverUploadOpen(true);
                                    }}
                                    className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl"
                                >
                                    Upload Cover Image
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-[300px] overflow-y-auto p-1">
                                {coverImagesList.map((img) => (
                                    <div
                                        key={img.id}
                                        onClick={() => {
                                            handleInputChange(null, "coverImage", img.image_url);
                                            setCoverPickerOpen(false);
                                        }}
                                        className={`group cursor-pointer border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all ${formData.coverImage === img.image_url
                                            ? "border-orange-500 ring-2 ring-orange-500/20"
                                            : "border-gray-100 hover:border-gray-300"
                                            }`}
                                    >
                                        <div className="relative h-24 w-full bg-gray-50 overflow-hidden">
                                            <img
                                                src={img.image_url}
                                                alt={img.name}
                                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-102"
                                                onError={(e) => {
                                                    e.target.src = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=300&q=80";
                                                }}
                                            />
                                        </div>
                                        <div className="p-3 bg-white">
                                            <h5 className="font-semibold text-gray-800 text-xs truncate">
                                                {img.name}
                                            </h5>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination Controls */}
                            {coverPickerTotal > 0 && (
                                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                    <div className="text-xs text-gray-500">
                                        Showing page <span className="font-semibold text-gray-800">{coverPickerPage}</span> of{" "}
                                        <span className="font-semibold text-gray-800">{Math.ceil(coverPickerTotal / 8)}</span> (
                                        <span className="font-semibold text-gray-800">{coverPickerTotal}</span> items)
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCoverPickerPage((p) => Math.max(p - 1, 1))}
                                            disabled={coverPickerPage === 1}
                                            className="rounded-xl text-xs py-1 px-3 h-8 border-gray-200"
                                        >
                                            Previous
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCoverPickerPage((p) => p + 1)}
                                            disabled={!coverPickerHasMore}
                                            className="rounded-xl text-xs py-1 px-3 h-8 border-gray-200"
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                <span className="text-xs text-gray-400">
                                    Total items matching: {coverPickerTotal}
                                </span>
                                <Button
                                    onClick={() => {
                                        setCoverPickerOpen(false);
                                        setCoverUploadOpen(true);
                                    }}
                                    className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-xs px-4 py-2"
                                >
                                    Upload New
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {showCreateCatalogModal && (
                <CatalogManagement
                    mode="modal"
                    isOpen={showCreateCatalogModal}
                    onClose={() => setShowCreateCatalogModal(false)}
                    defaultCategory={createCatalogCategory}
                    onSave={handleCatalogSaved}
                />
            )}
        </div>
    );
};

export default QuotationBuilder;
